import React, { useState } from 'react';
import { 
  Book, 
  Shield, 
  Database, 
  Zap, 
  Users, 
  Lock, 
  Code, 
  Activity,
  Filter,
  CheckCircle,
  ChevronRight,
  ChevronDown
} from 'lucide-react';

export default function Documentation() {
  const [activeSection, setActiveSection] = useState('overview');
  const [expandedSections, setExpandedSections] = useState(['getting-started']);

  const toggleSection = (sectionId) => {
    if (expandedSections.includes(sectionId)) {
      setExpandedSections(expandedSections.filter(id => id !== sectionId));
    } else {
      setExpandedSections([...expandedSections, sectionId]);
    }
  };

  const documentation = {
    'getting-started': {
      title: 'Getting Started',
      icon: Book,
      sections: [
        { id: 'overview', title: 'Platform Overview' },
        { id: 'architecture', title: 'System Architecture' },
        { id: 'quick-start', title: 'Quick Start Guide' }
      ]
    },
    'core-features': {
      title: 'Core Features',
      icon: Zap,
      sections: [
        { id: 'sri-index', title: 'Sports Readiness Index (SRI)' },
        { id: 'assessments', title: 'Physical Assessments' },
        { id: 'ai-insights', title: 'AI-Powered Insights' },
        { id: 'manual-explorer', title: 'Manual Data Explorer' }
      ]
    },
    'splink-id': {
      title: 'Splink ID System',
      icon: Users,
      sections: [
        { id: 'splink-overview', title: 'Splink ID Overview' },
        { id: 'splink-generation', title: 'ID Generation' },
        { id: 'splink-invitations', title: 'Athlete Invitations' },
        { id: 'splink-reports', title: 'Report Management' }
      ]
    },
    'external-data': {
      title: 'External Data Connector',
      icon: Database,
      sections: [
        { id: 'external-overview', title: 'Overview' },
        { id: 'database-schema', title: 'Database Schema Reference' },
        { id: 'connection-manual', title: 'Connection Manual' },
        { id: 'live-connections', title: 'Live Database Connections' },
        { id: 'data-imports', title: 'CSV/Excel Imports' },
        { id: 'schema-mapping', title: 'Schema Mapping' }
      ]
    },
    'security': {
      title: 'Security & Privacy',
      icon: Shield,
      sections: [
        { id: 'authentication', title: 'Authentication (Stack Auth)' },
        { id: 'data-encryption', title: 'Data Encryption' },
        { id: 'access-control', title: 'Role-Based Access Control' },
        { id: 'audit-logging', title: 'Audit Logging' },
        { id: 'data-privacy', title: 'Data Privacy & GDPR' }
      ]
    },
    'api-reference': {
      title: 'API Reference',
      icon: Code,
      sections: [
        { id: 'api-overview', title: 'API Overview' },
        { id: 'api-athletes', title: 'Athletes API' },
        { id: 'api-assessments', title: 'Assessments API' },
        { id: 'api-external-db', title: 'External DB API' },
        { id: 'api-data-import', title: 'Data Import API' }
      ]
    },
    'analytics': {
      title: 'Advanced Analytics',
      icon: Activity,
      sections: [
        { id: 'correlation-engine', title: 'Correlation Engine' },
        { id: 'visualization', title: 'Data Visualization' },
        { id: 'export-reports', title: 'Export & Reports' }
      ]
    }
  };

  const renderContent = () => {
    const contentMap = {
      'overview': <OverviewContent />,
      'architecture': <ArchitectureContent />,
      'quick-start': <QuickStartContent />,
      'sri-index': <SRIIndexContent />,
      'assessments': <AssessmentsContent />,
      'ai-insights': <AIInsightsContent />,
      'manual-explorer': <ManualExplorerContent />,
      'splink-overview': <SplinkOverviewContent />,
      'splink-generation': <SplinkGenerationContent />,
      'splink-invitations': <SplinkInvitationsContent />,
      'splink-reports': <SplinkReportsContent />,
      'external-overview': <ExternalOverviewContent />,
      'database-schema': <DatabaseSchemaContent />,
      'connection-manual': <ConnectionManualContent />,
      'live-connections': <LiveConnectionsContent />,
      'data-imports': <DataImportsContent />,
      'schema-mapping': <SchemaMappingContent />,
      'authentication': <AuthenticationContent />,
      'data-encryption': <DataEncryptionContent />,
      'access-control': <AccessControlContent />,
      'audit-logging': <AuditLoggingContent />,
      'data-privacy': <DataPrivacyContent />,
      'api-overview': <APIOverviewContent />,
      'api-athletes': <APIAthletesContent />,
      'api-assessments': <APIAssessmentsContent />,
      'api-external-db': <APIExternalDBContent />,
      'api-data-import': <APIDataImportContent />,
      'correlation-engine': <CorrelationEngineContent />,
      'visualization': <VisualizationContent />,
      'export-reports': <ExportReportsContent />
    };

    return contentMap[activeSection] || <OverviewContent />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="flex h-screen">
        {/* Sidebar */}
        <div className="w-80 bg-slate-800/50 backdrop-blur-sm border-r border-slate-700 overflow-y-auto">
          <div className="p-6 border-b border-slate-700">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-500/20 rounded-lg p-2">
                <Book className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Documentation</h1>
                <p className="text-xs text-slate-400">SRI Platform v1.0</p>
              </div>
            </div>
          </div>

          <nav className="p-4">
            {Object.entries(documentation).map(([key, category]) => {
              const Icon = category.icon;
              const isExpanded = expandedSections.includes(key);

              return (
                <div key={key} className="mb-2">
                  <button
                    onClick={() => toggleSection(key)}
                    className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-slate-700/50 transition-colors text-left"
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className="w-5 h-5 text-blue-400" />
                      <span className="text-white font-medium">{category.title}</span>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    )}
                  </button>

                  {isExpanded && (
                    <div className="ml-8 mt-1 space-y-1">
                      {category.sections.map((section) => (
                        <button
                          key={section.id}
                          onClick={() => setActiveSection(section.id)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                            activeSection === section.id
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
                          }`}
                        >
                          {section.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto p-8">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
}

// Content Components
function OverviewContent() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold text-white mb-2">
          Splink Readiness Index (SRI) Platform
        </h1>
        <p className="text-lg text-slate-400">
          Comprehensive sports talent identification and athlete development platform
        </p>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">What is SRI?</h2>
        <p className="text-slate-300 mb-4">
          The Sports Readiness Index (SRI) is an advanced analytics platform designed to identify, assess, and develop athletic talent across multiple sports disciplines. Built on a foundation of scientific research and data-driven insights, SRI provides comprehensive athlete profiling, performance tracking, and predictive analytics.
        </p>
        <p className="text-slate-300">
          The platform combines physical assessments, cognitive evaluations, and AI-powered analysis to create a holistic view of athlete potential and readiness across various sports.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/30 p-6">
          <div className="flex items-center space-x-3 mb-3">
            <Activity className="w-6 h-6 text-blue-400" />
            <h3 className="text-lg font-semibold text-white">10,000+</h3>
          </div>
          <p className="text-slate-400">Athletes Assessed</p>
        </div>

        <div className="bg-gradient-to-br from-green-500/10 to-blue-500/10 rounded-xl border border-green-500/30 p-6">
          <div className="flex items-center space-x-3 mb-3">
            <Database className="w-6 h-6 text-green-400" />
            <h3 className="text-lg font-semibold text-white">50+</h3>
          </div>
          <p className="text-slate-400">Performance Metrics</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/30 p-6">
          <div className="flex items-center space-x-3 mb-3">
            <Zap className="w-6 h-6 text-purple-400" />
            <h3 className="text-lg font-semibold text-white">AI-Powered</h3>
          </div>
          <p className="text-slate-400">Natural Language Insights</p>
        </div>

        <div className="bg-gradient-to-br from-pink-500/10 to-red-500/10 rounded-xl border border-pink-500/30 p-6">
          <div className="flex items-center space-x-3 mb-3">
            <Shield className="w-6 h-6 text-pink-400" />
            <h3 className="text-lg font-semibold text-white">Enterprise</h3>
          </div>
          <p className="text-slate-400">Security & Privacy</p>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Key Features</h2>
        <div className="space-y-3">
          {[
            'Multi-sport athlete profiling and assessment',
            'AI-powered natural language query interface',
            'External database connectivity (on-premises & cloud)',
            'CSV/Excel data import with auto schema mapping',
            'Splink ID system for athlete identification',
            'Role-based access control and audit logging',
            'Advanced correlation engine for sport-specific insights',
            'Real-time data visualization and export'
          ].map((feature, idx) => (
            <div key={idx} className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
              <span className="text-slate-300">{feature}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ArchitectureContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">System Architecture</h1>
      <p className="text-lg text-slate-400 mb-6">
        Hybrid cloud architecture with on-premises connectivity
      </p>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Technology Stack</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-blue-400 mb-2">Frontend</h3>
            <div className="flex flex-wrap gap-2">
              {['React 18', 'TailwindCSS', 'Lucide Icons', 'Recharts', 'Stack Auth'].map(tech => (
                <span key={tech} className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm">
                  {tech}
                </span>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-green-400 mb-2">Backend</h3>
            <div className="flex flex-wrap gap-2">
              {['Node.js', 'Vercel Serverless', 'PostgreSQL (Neon)', 'OpenAI GPT-4'].map(tech => (
                <span key={tech} className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                  {tech}
                </span>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-purple-400 mb-2">External Connectivity</h3>
            <div className="flex flex-wrap gap-2">
              {['SSH2', 'MySQL2', 'Tedious (SQL Server)', 'Tunnel-SSH', 'PapaCSV', 'XLSX'].map(tech => (
                <span key={tech} className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Architecture Diagram</h2>
        <div className="bg-slate-900 rounded-lg p-6 font-mono text-sm text-slate-300">
          <pre>{`
┌─────────────────────────────────────────────────────────┐
│                    Client Browser                        │
│              (React + TailwindCSS + Stack Auth)         │
└────────────────────┬────────────────────────────────────┘
                     │
                     ↓
┌─────────────────────────────────────────────────────────┐
│                 Vercel Edge Network                      │
│            (CDN + Serverless Functions)                  │
└────────┬───────────────────────────────┬────────────────┘
         │                               │
         ↓                               ↓
┌────────────────────┐         ┌────────────────────────┐
│   API Handlers     │         │  External Connectors   │
│  - Athletes API    │         │  - SSH Tunnels         │
│  - AI Insights     │         │  - MySQL/PostgreSQL    │
│  - Data Import     │         │  - SQL Server          │
│  - External DB     │         │  - CSV/Excel Parser    │
└────────┬───────────┘         └────────┬───────────────┘
         │                               │
         ↓                               ↓
┌────────────────────┐         ┌────────────────────────┐
│  Neon PostgreSQL   │         │  External Databases    │
│  - Athletes        │         │  - School DBs          │
│  - Assessments     │         │  - District Systems    │
│  - SRI Scores      │         │  - Partner Data        │
│  - Splink IDs      │         │  - On-Premises SQL     │
│  - Import Tables   │         └────────────────────────┘
└────────────────────┘
          `}</pre>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Data Flow</h2>
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="bg-blue-500/20 rounded-full p-2 mt-1">
              <span className="text-blue-400 font-bold">1</span>
            </div>
            <div>
              <h4 className="text-white font-semibold">User Authentication</h4>
              <p className="text-slate-400 text-sm">Stack Auth handles OAuth, session management, and user roles</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="bg-green-500/20 rounded-full p-2 mt-1">
              <span className="text-green-400 font-bold">2</span>
            </div>
            <div>
              <h4 className="text-white font-semibold">API Request</h4>
              <p className="text-slate-400 text-sm">Client sends request to Vercel serverless function</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="bg-purple-500/20 rounded-full p-2 mt-1">
              <span className="text-purple-400 font-bold">3</span>
            </div>
            <div>
              <h4 className="text-white font-semibold">Data Query</h4>
              <p className="text-slate-400 text-sm">API queries internal DB or establishes external connection</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="bg-pink-500/20 rounded-full p-2 mt-1">
              <span className="text-pink-400 font-bold">4</span>
            </div>
            <div>
              <h4 className="text-white font-semibold">Response & Render</h4>
              <p className="text-slate-400 text-sm">Data transformed, cached, and rendered in React components</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickStartContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Quick Start Guide</h1>
      <p className="text-lg text-slate-400 mb-6">
        Get up and running with SRI Platform in minutes
      </p>

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-blue-400 mb-2">Prerequisites</h3>
        <ul className="list-disc list-inside text-slate-300 space-y-1">
          <li>Admin account with proper role assignment</li>
          <li>Database connection credentials (if using external data)</li>
          <li>CSV/Excel files formatted according to SRI schema (for imports)</li>
        </ul>
      </div>

      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-white">Step 1: Authentication</h2>
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
          <p className="text-slate-300 mb-4">
            Log in using Stack Auth OAuth. Supported providers:
          </p>
          <div className="flex gap-3">
            {['Google', 'GitHub', 'Email'].map(provider => (
              <div key={provider} className="px-4 py-2 bg-slate-700 rounded-lg text-white">
                {provider}
              </div>
            ))}
          </div>
        </div>

        <h2 className="text-2xl font-bold text-white mt-6">Step 2: Navigate Dashboard</h2>
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-500/20 rounded flex items-center justify-center">
                <span className="text-blue-400">📊</span>
              </div>
              <div>
                <p className="text-white font-medium">Manual Explorer</p>
                <p className="text-sm text-slate-400">Browse and filter athlete data</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-green-500/20 rounded flex items-center justify-center">
                <span className="text-green-400">🤖</span>
              </div>
              <div>
                <p className="text-white font-medium">AI Insights</p>
                <p className="text-sm text-slate-400">Natural language queries</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-purple-500/20 rounded flex items-center justify-center">
                <span className="text-purple-400">🔌</span>
              </div>
              <div>
                <p className="text-white font-medium">External DB</p>
                <p className="text-sm text-slate-400">Connect external databases or import data</p>
              </div>
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-white mt-6">Step 3: Import Your Data</h2>
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
          <div className="space-y-4">
            <p className="text-slate-300">
              Go to <strong className="text-white">External DB → Data Imports</strong> tab
            </p>
            <div className="bg-slate-900 rounded-lg p-4">
              <code className="text-green-400">
                1. Click "Import Dataset"<br/>
                2. Upload CSV/Excel file<br/>
                3. Review schema validation<br/>
                4. Configure mappings<br/>
                5. Import data
              </code>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Placeholder components for other sections (to be expanded)
function SRIIndexContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Sports Readiness Index (SRI)</h1>
      <p className="text-lg text-slate-400">
        Comprehensive athlete scoring system across multiple sports
      </p>
      {/* Content to be added */}
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
        <p className="text-yellow-400">📝 Detailed content coming soon...</p>
      </div>
    </div>
  );
}

function AssessmentsContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Physical Assessments</h1>
      <p className="text-lg text-slate-400 mb-6">
        Comprehensive physical testing protocols for athlete evaluation
      </p>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Assessment Categories</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/30 p-4">
            <h3 className="text-white font-semibold mb-2">Anthropometric</h3>
            <ul className="text-sm text-slate-400 space-y-1">
              <li>• Height & Weight</li>
              <li>• Wingspan & Arm Length</li>
              <li>• Sitting Height</li>
              <li>• BMI & Body Composition</li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-green-500/10 to-blue-500/10 rounded-lg border border-green-500/30 p-4">
            <h3 className="text-white font-semibold mb-2">Speed & Agility</h3>
            <ul className="text-sm text-slate-400 space-y-1">
              <li>• 30m Sprint</li>
              <li>• 100m Sprint</li>
              <li>• 400m Sprint</li>
              <li>• Shuttle Run & T-Test</li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-lg border border-purple-500/30 p-4">
            <h3 className="text-white font-semibold mb-2">Power & Strength</h3>
            <ul className="text-sm text-slate-400 space-y-1">
              <li>• Vertical Jump</li>
              <li>• Broad Jump</li>
              <li>• Shot Put Distance</li>
              <li>• Grip Strength</li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-pink-500/10 to-red-500/10 rounded-lg border border-pink-500/30 p-4">
            <h3 className="text-white font-semibold mb-2">Sport-Specific</h3>
            <ul className="text-sm text-slate-400 space-y-1">
              <li>• Football Free Kick</li>
              <li>• Cricket Throw</li>
              <li>• Sit & Reach (Flexibility)</li>
              <li>• Custom Sport Tests</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Derived Metrics</h2>
        <p className="text-slate-300 mb-4">
          SRI automatically calculates advanced performance indicators:
        </p>
        <div className="space-y-3">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">Ape Index</h4>
            <p className="text-sm text-slate-400">Wingspan minus height - indicates reach advantage (important for basketball, volleyball, boxing)</p>
            <code className="text-xs text-green-400">Formula: wingspan_cm - height_cm</code>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">Explosiveness Ratio</h4>
            <p className="text-sm text-slate-400">Vertical jump relative to broad jump - measures explosive power consistency</p>
            <code className="text-xs text-green-400">Formula: vertical_jump_cm / (broad_jump_cm / 4)</code>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">Power-to-Weight Ratio</h4>
            <p className="text-sm text-slate-400">Broad jump distance relative to body weight - key for sprinting and jumping sports</p>
            <code className="text-xs text-green-400">Formula: broad_jump_cm / weight_kg</code>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">Torso Ratio</h4>
            <p className="text-sm text-slate-400">Sitting height to total height - indicates body proportions for sport suitability</p>
            <code className="text-xs text-green-400">Formula: sitting_height_cm / height_cm</code>
          </div>
        </div>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
        <h3 className="text-blue-400 font-semibold mb-3">Assessment Workflow</h3>
        <div className="space-y-2 text-slate-300">
          <p>1. <strong>Pre-Assessment:</strong> Record athlete demographics and consent</p>
          <p>2. <strong>Anthropometric Tests:</strong> Measure physical dimensions</p>
          <p>3. <strong>Performance Tests:</strong> Conduct speed, power, and agility assessments</p>
          <p>4. <strong>Sport-Specific Tests:</strong> Evaluate sport-relevant skills</p>
          <p>5. <strong>Data Entry:</strong> Input results into SRI platform</p>
          <p>6. <strong>Analysis:</strong> System calculates derived metrics and generates SRI scores</p>
        </div>
      </div>
    </div>
  );
}

function AIInsightsContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">AI-Powered Insights</h1>
      <p className="text-lg text-slate-400 mb-6">
        Natural language query interface powered by OpenAI GPT-4
      </p>

      <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/30 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Zap className="w-8 h-8 text-purple-400" />
          <h2 className="text-2xl font-bold text-white">Ask Questions in Plain English</h2>
        </div>
        <p className="text-slate-300 mb-4">
          No SQL knowledge required. Simply ask questions about your athletes and get instant insights.
        </p>
        <div className="bg-slate-900 rounded-lg p-4">
          <p className="text-sm text-slate-400 mb-2">Example queries:</p>
          <div className="space-y-2">
            <div className="bg-slate-800 rounded px-3 py-2 text-green-400 text-sm font-mono">
              "Show me all athletes from Patna with vertical jump over 40cm"
            </div>
            <div className="bg-slate-800 rounded px-3 py-2 text-green-400 text-sm font-mono">
              "Find male athletes aged 15-17 who excel in sprinting"
            </div>
            <div className="bg-slate-800 rounded px-3 py-2 text-green-400 text-sm font-mono">
              "Which districts have the highest average BMI?"
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">How It Works</h2>
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="bg-blue-500/20 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-blue-400 font-bold">1</span>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-1">Natural Language Processing</h4>
              <p className="text-slate-400 text-sm">GPT-4 analyzes your question and understands the intent</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="bg-green-500/20 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-green-400 font-bold">2</span>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-1">SQL Generation</h4>
              <p className="text-slate-400 text-sm">AI converts your question into optimized SQL query</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="bg-purple-500/20 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-purple-400 font-bold">3</span>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-1">Query Execution</h4>
              <p className="text-slate-400 text-sm">System executes query against your selected data source</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="bg-pink-500/20 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-pink-400 font-bold">4</span>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-1">Results & Visualization</h4>
              <p className="text-slate-400 text-sm">Data displayed in tables and charts with AI-generated insights</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Advanced Features</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">Multi-Table Joins</h4>
            <p className="text-sm text-slate-400">Query across athletes, schools, districts, and assessments</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-2">Aggregations</h4>
            <p className="text-sm text-slate-400">Calculate averages, counts, min/max across groups</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">Filtering</h4>
            <p className="text-sm text-slate-400">Complex conditions with AND/OR logic</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-pink-400 font-semibold mb-2">Sorting & Ranking</h4>
            <p className="text-sm text-slate-400">Order results by any metric, find top performers</p>
          </div>
        </div>
      </div>

      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
        <h3 className="text-yellow-400 font-semibold mb-3 flex items-center space-x-2">
          <Shield className="w-5 h-5" />
          <span>Security & Privacy</span>
        </h3>
        <ul className="space-y-2 text-slate-300 text-sm">
          <li>• All queries are validated before execution</li>
          <li>• Read-only access prevents data modification</li>
          <li>• Queries logged for audit compliance</li>
          <li>• Role-based access control enforced</li>
        </ul>
      </div>
    </div>
  );
}

function ManualExplorerContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Manual Explorer</h1>
      <p className="text-lg text-slate-400 mb-6">
        Advanced filtering and data exploration interface
      </p>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Key Features</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/30 p-4">
            <h3 className="text-white font-semibold mb-2 flex items-center space-x-2">
              <Filter className="w-5 h-5 text-blue-400" />
              <span>Dynamic Filters</span>
            </h3>
            <p className="text-sm text-slate-400">Add multiple filters for district, gender, age range, height, weight, and performance metrics</p>
          </div>

          <div className="bg-gradient-to-br from-green-500/10 to-blue-500/10 rounded-lg border border-green-500/30 p-4">
            <h3 className="text-white font-semibold mb-2 flex items-center space-x-2">
              <Database className="w-5 h-5 text-green-400" />
              <span>Multi-Source Data</span>
            </h3>
            <p className="text-sm text-slate-400">Query internal database, imported datasets, or external live connections</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-lg border border-purple-500/30 p-4">
            <h3 className="text-white font-semibold mb-2 flex items-center space-x-2">
              <Activity className="w-5 h-5 text-purple-400" />
              <span>Real-Time Stats</span>
            </h3>
            <p className="text-sm text-slate-400">Live statistics dashboard showing gender distribution, averages, and ranges</p>
          </div>

          <div className="bg-gradient-to-br from-pink-500/10 to-red-500/10 rounded-lg border border-pink-500/30 p-4">
            <h3 className="text-white font-semibold mb-2 flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-pink-400" />
              <span>Export & Share</span>
            </h3>
            <p className="text-sm text-slate-400">Export filtered results to CSV for further analysis</p>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Available Filters</h2>
        <div className="space-y-3">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">Geographic</h4>
            <p className="text-sm text-slate-400">Filter by district, town, or school</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-2">Demographics</h4>
            <p className="text-sm text-slate-400">Gender, age range, date of birth</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">Anthropometric</h4>
            <p className="text-sm text-slate-400">Height, weight, BMI, wingspan ranges</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-pink-400 font-semibold mb-2">Performance</h4>
            <p className="text-sm text-slate-400">Sprint times, jump distances, strength metrics</p>
          </div>
        </div>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
        <h3 className="text-blue-400 font-semibold mb-3">Data Source Selection</h3>
        <p className="text-slate-300 mb-3">Choose from multiple data sources:</p>
        <div className="space-y-2 text-sm">
          <div className="flex items-center space-x-2">
            <span className="text-2xl">🏠</span>
            <span className="text-white font-medium">Internal SRI Database</span>
            <span className="text-slate-400">- 10,000+ athletes</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-2xl">📊</span>
            <span className="text-white font-medium">Imported Datasets</span>
            <span className="text-slate-400">- CSV/Excel uploads</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-2xl">🔌</span>
            <span className="text-white font-medium">External Databases</span>
            <span className="text-slate-400">- Live connections</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SplinkOverviewContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Splink ID System</h1>
      <p className="text-lg text-slate-400 mb-6">
        Unique athlete identification and engagement platform
      </p>

      <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/30 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">What is Splink ID?</h2>
        <p className="text-slate-300 mb-4">
          Splink ID is a unique identifier assigned to each athlete in the SRI system. It serves as a permanent digital identity that connects athletes to their assessment history, performance data, and personalized reports.
        </p>
        <p className="text-slate-300">
          Athletes can claim their Splink ID to access their own data, track progress over time, and receive personalized training recommendations.
        </p>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Key Benefits</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">🎯 Permanent Identity</h4>
            <p className="text-sm text-slate-400">One ID for life - tracks athlete journey from youth to professional</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-2">📊 Data Ownership</h4>
            <p className="text-sm text-slate-400">Athletes own their data and control who can access it</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">📈 Progress Tracking</h4>
            <p className="text-sm text-slate-400">Historical data shows improvement over time</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-pink-400 font-semibold mb-2">🔐 Privacy Protected</h4>
            <p className="text-sm text-slate-400">Secure authentication with Stack Auth</p>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">How It Works</h2>
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <div className="bg-blue-500/20 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-blue-400 font-bold">1</span>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-1">Generation</h4>
              <p className="text-slate-400 text-sm">Splink ID automatically generated when athlete is added to system</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="bg-green-500/20 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-green-400 font-bold">2</span>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-1">Invitation</h4>
              <p className="text-slate-400 text-sm">Email invitation sent to athlete with unique claim link</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="bg-purple-500/20 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-purple-400 font-bold">3</span>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-1">Activation</h4>
              <p className="text-slate-400 text-sm">Athlete creates account and claims their Splink ID</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="bg-pink-500/20 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-pink-400 font-bold">4</span>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-1">Access</h4>
              <p className="text-slate-400 text-sm">Athlete can view reports, track progress, and update profile</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SplinkGenerationContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Splink ID Generation</h1>
      <p className="text-lg text-slate-400 mb-6">
        Automatic generation of unique athlete identifiers
      </p>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Generation Process</h2>
        <p className="text-slate-300 mb-4">
          Splink IDs are automatically generated using UUID v4 format, ensuring global uniqueness and preventing collisions.
        </p>
        <div className="bg-slate-900 rounded-lg p-4">
          <p className="text-sm text-slate-400 mb-2">Example Splink ID:</p>
          <code className="text-green-400 font-mono">splink_a3f2d8e9-4b7c-4d1e-9f8a-2c5b6e7d8f9a</code>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">When IDs are Generated</h2>
        <div className="space-y-3">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">New Athlete Entry</h4>
            <p className="text-sm text-slate-400">Automatically generated when athlete record is created in the system</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-2">Data Import</h4>
            <p className="text-sm text-slate-400">Generated for athletes imported from CSV/Excel files</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">External Database Sync</h4>
            <p className="text-sm text-slate-400">Created when syncing athletes from external databases</p>
          </div>
        </div>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
        <h3 className="text-blue-400 font-semibold mb-3">Technical Details</h3>
        <ul className="space-y-2 text-slate-300 text-sm">
          <li>• Format: UUID v4 with 'splink_' prefix</li>
          <li>• Uniqueness: Cryptographically guaranteed</li>
          <li>• Immutable: Once generated, never changes</li>
          <li>• Indexed: Optimized for fast lookups</li>
        </ul>
      </div>
    </div>
  );
}

function SplinkInvitationsContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Splink Invitations</h1>
      <p className="text-lg text-slate-400 mb-6">
        Email-based athlete onboarding system
      </p>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Invitation Flow</h2>
        <div className="space-y-4">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">1. Admin Sends Invitation</h4>
            <p className="text-sm text-slate-400 mb-2">From Manual Explorer, admin selects athletes and clicks "Send Splink Invitations"</p>
            <div className="bg-slate-800 rounded p-3 text-xs">
              <p className="text-slate-500 mb-1">Required information:</p>
              <ul className="list-disc list-inside text-slate-400">
                <li>Athlete email address</li>
                <li>Athlete name</li>
                <li>Splink ID</li>
              </ul>
            </div>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-2">2. Email Delivered</h4>
            <p className="text-sm text-slate-400">Invitation email sent via Resend API with personalized content</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">3. Athlete Claims ID</h4>
            <p className="text-sm text-slate-400">Athlete clicks link, creates account, and claims their Splink ID</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-pink-400 font-semibold mb-2">4. Account Activated</h4>
            <p className="text-sm text-slate-400">Athlete can now access their personalized dashboard and reports</p>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Email Template</h2>
        <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
          <p className="text-sm text-slate-400 mb-3">Sample invitation email:</p>
          <div className="bg-slate-800 rounded p-4 space-y-2 text-sm">
            <p className="text-white font-semibold">Subject: Your SRI Splink ID is Ready!</p>
            <div className="border-t border-slate-700 my-2"></div>
            <p className="text-slate-300">Hi [Athlete Name],</p>
            <p className="text-slate-300">Your Sports Readiness Index profile has been created. Click below to claim your Splink ID and access your personalized reports:</p>
            <div className="bg-blue-500/20 rounded px-3 py-2 text-blue-400 text-center my-2">
              Claim Your Splink ID
            </div>
            <p className="text-slate-400 text-xs">Your Splink ID: [splink_xxx...]</p>
          </div>
        </div>
      </div>

      <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6">
        <h3 className="text-green-400 font-semibold mb-3">Tracking & Status</h3>
        <p className="text-slate-300 mb-3">Monitor invitation status in real-time:</p>
        <ul className="space-y-2 text-slate-300 text-sm">
          <li>• <strong>Pending:</strong> Invitation not yet sent</li>
          <li>• <strong>Sent:</strong> Email delivered successfully</li>
          <li>• <strong>Claimed:</strong> Athlete activated their account</li>
          <li>• <strong>Failed:</strong> Email delivery failed (invalid address)</li>
        </ul>
      </div>
    </div>
  );
}

function SplinkReportsContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Splink Reports</h1>
      <p className="text-lg text-slate-400 mb-6">
        Personalized athlete performance reports
      </p>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Report Types</h2>
        <div className="space-y-3">
          <div className="bg-gradient-to-r from-blue-500/10 to-blue-500/5 rounded-lg border border-blue-500/30 p-4">
            <h4 className="text-blue-400 font-semibold mb-2">📊 Comprehensive Assessment Report</h4>
            <p className="text-sm text-slate-400 mb-2">Complete overview of all physical assessments and metrics</p>
            <ul className="text-xs text-slate-400 space-y-1 ml-4">
              <li>• Anthropometric measurements</li>
              <li>• Speed and agility results</li>
              <li>• Power and strength scores</li>
              <li>• Sport-specific performance</li>
            </ul>
          </div>

          <div className="bg-gradient-to-r from-green-500/10 to-green-500/5 rounded-lg border border-green-500/30 p-4">
            <h4 className="text-green-400 font-semibold mb-2">🎯 Sport Suitability Analysis</h4>
            <p className="text-sm text-slate-400 mb-2">AI-powered recommendations for optimal sports</p>
            <ul className="text-xs text-slate-400 space-y-1 ml-4">
              <li>• Top 3 recommended sports</li>
              <li>• Correlation scores for each sport</li>
              <li>• Strengths and areas for improvement</li>
            </ul>
          </div>

          <div className="bg-gradient-to-r from-purple-500/10 to-purple-500/5 rounded-lg border border-purple-500/30 p-4">
            <h4 className="text-purple-400 font-semibold mb-2">📈 Progress Tracking Report</h4>
            <p className="text-sm text-slate-400 mb-2">Historical comparison of assessments over time</p>
            <ul className="text-xs text-slate-400 space-y-1 ml-4">
              <li>• Performance trends and improvements</li>
              <li>• Percentile rankings</li>
              <li>• Growth trajectory predictions</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Report Features</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">📱 Mobile Friendly</h4>
            <p className="text-sm text-slate-400">Responsive design works on all devices</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-2">💾 PDF Export</h4>
            <p className="text-sm text-slate-400">Download reports for offline viewing</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">🔄 Auto-Update</h4>
            <p className="text-sm text-slate-400">Reports refresh with new assessment data</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-pink-400 font-semibold mb-2">🔐 Secure Access</h4>
            <p className="text-sm text-slate-400">Only athlete can view their own reports</p>
          </div>
        </div>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
        <h3 className="text-blue-400 font-semibold mb-3">Accessing Reports</h3>
        <div className="space-y-2 text-slate-300 text-sm">
          <p>1. Athlete logs in with Splink ID credentials</p>
          <p>2. Dashboard shows latest assessment summary</p>
          <p>3. Click "View Full Report" for detailed analysis</p>
          <p>4. Download PDF or share link with coaches</p>
        </div>
      </div>
    </div>
  );
}

function ExternalOverviewContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">External Data Connector</h1>
      <p className="text-lg text-slate-400 mb-6">
        Connect and import data from external sources
      </p>

      <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/30 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Why External Data?</h2>
        <p className="text-slate-300 mb-4">
          Many organizations already have athlete data in their own databases or spreadsheets. The External Data Connector allows you to bring that data into SRI without manual re-entry.
        </p>
        <p className="text-slate-300">
          This enables unified analytics across multiple data sources while maintaining data sovereignty and security.
        </p>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Two Connection Methods</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/30 p-6">
            <div className="text-4xl mb-3">🔌</div>
            <h3 className="text-white font-semibold mb-2">Live Database Connections</h3>
            <p className="text-sm text-slate-400 mb-3">Real-time access to external SQL databases</p>
            <ul className="text-xs text-slate-400 space-y-1">
              <li>• PostgreSQL, MySQL, SQL Server</li>
              <li>• SSH tunnel support</li>
              <li>• Read-only access</li>
              <li>• Always up-to-date data</li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-green-500/10 to-blue-500/10 rounded-lg border border-green-500/30 p-6">
            <div className="text-4xl mb-3">📊</div>
            <h3 className="text-white font-semibold mb-2">Data Imports</h3>
            <p className="text-sm text-slate-400 mb-3">Upload CSV, Excel, or JSON files</p>
            <ul className="text-xs text-slate-400 space-y-1">
              <li>• One-time or periodic imports</li>
              <li>• Auto schema validation</li>
              <li>• Fuzzy column matching</li>
              <li>• Stored in separate tables</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Key Features</h2>
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
            <div>
              <p className="text-white font-medium">Unified Data Source Dropdown</p>
              <p className="text-sm text-slate-400">Switch between internal, imported, and external data seamlessly</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
            <div>
              <p className="text-white font-medium">Schema Validation</p>
              <p className="text-sm text-slate-400">Automatic validation against SRI schema with helpful error messages</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
            <div>
              <p className="text-white font-medium">Security First</p>
              <p className="text-sm text-slate-400">Encrypted credentials, SSH tunnels, read-only access, audit logging</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
            <div>
              <p className="text-white font-medium">No Data Lock-In</p>
              <p className="text-sm text-slate-400">Your data stays in your database - we just query it</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConnectionManualContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Connection Manual</h1>
      <p className="text-lg text-slate-400 mb-6">
        Step-by-step guide to securely connect your database to SRI Platform
      </p>

      {/* Prerequisites */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
        <div className="flex items-start space-x-3">
          <Lock className="w-6 h-6 text-blue-400 mt-1" />
          <div>
            <h3 className="text-lg font-semibold text-blue-400 mb-2">Before You Start</h3>
            <ul className="space-y-2 text-slate-300">
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-400 mt-1" />
                <span>Database credentials (host, port, username, password)</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-400 mt-1" />
                <span>SSH server details (if connecting to on-premises database)</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-400 mt-1" />
                <span>Read-only database user (recommended for security)</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-400 mt-1" />
                <span>Firewall rules configured (if applicable)</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Step 1: Access Connection Wizard */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="bg-blue-500/20 rounded-full w-8 h-8 flex items-center justify-center">
            <span className="text-blue-400 font-bold">1</span>
          </div>
          <h2 className="text-2xl font-bold text-white">Access the Connection Wizard</h2>
        </div>
        
        <div className="space-y-4">
          <p className="text-slate-300">
            Navigate to the External DB tab and click "Add Connection" button.
          </p>
          
          <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
            <p className="text-sm text-slate-400 mb-2">Navigation Path:</p>
            <code className="text-green-400">
              DB Analytics → External DB → Live Connections → Add Connection
            </code>
          </div>

          <div className="bg-slate-700/30 rounded-lg p-4">
            <p className="text-slate-300 text-sm">
              💡 <strong>Tip:</strong> The wizard has 4 steps and will guide you through the entire process. You can go back at any time.
            </p>
          </div>
        </div>
      </div>

      {/* Step 2: Select Database Type */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="bg-green-500/20 rounded-full w-8 h-8 flex items-center justify-center">
            <span className="text-green-400 font-bold">2</span>
          </div>
          <h2 className="text-2xl font-bold text-white">Select Your Database Type</h2>
        </div>

        <p className="text-slate-300 mb-4">
          Choose the type of database you want to connect. Currently supported:
        </p>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/30 p-4">
            <div className="text-3xl mb-2">🐘</div>
            <h4 className="text-white font-semibold mb-1">PostgreSQL</h4>
            <p className="text-sm text-slate-400">Default port: 5432</p>
          </div>

          <div className="bg-gradient-to-br from-green-500/10 to-blue-500/10 rounded-lg border border-green-500/30 p-4">
            <div className="text-3xl mb-2">🐬</div>
            <h4 className="text-white font-semibold mb-1">MySQL</h4>
            <p className="text-sm text-slate-400">Default port: 3306</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-lg border border-purple-500/30 p-4">
            <div className="text-3xl mb-2">🗄️</div>
            <h4 className="text-white font-semibold mb-1">SQL Server</h4>
            <p className="text-sm text-slate-400">Default port: 1433</p>
          </div>

          <div className="bg-slate-700/30 rounded-lg border border-slate-600 p-4 opacity-50">
            <div className="text-3xl mb-2">🔶</div>
            <h4 className="text-slate-400 font-semibold mb-1">Oracle</h4>
            <p className="text-sm text-slate-500">Coming Soon</p>
          </div>
        </div>

        <div className="mt-4 bg-slate-700/30 rounded-lg p-4">
          <p className="text-slate-300 text-sm">
            ⚡ <strong>Quick Tip:</strong> The wizard will automatically set the default port for your selected database type.
          </p>
        </div>
      </div>

      {/* Step 3: Choose Connection Method */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="bg-purple-500/20 rounded-full w-8 h-8 flex items-center justify-center">
            <span className="text-purple-400 font-bold">3</span>
          </div>
          <h2 className="text-2xl font-bold text-white">Choose Connection Method</h2>
        </div>

        <div className="space-y-4">
          <div className="bg-gradient-to-r from-blue-500/10 to-blue-500/5 rounded-lg border border-blue-500/30 p-4">
            <h4 className="text-blue-400 font-semibold mb-2 flex items-center space-x-2">
              <span>🌐</span>
              <span>Direct Connection</span>
            </h4>
            <p className="text-slate-300 text-sm mb-3">
              Use this when your database has a public IP address or is accessible from the internet.
            </p>
            <div className="bg-slate-900 rounded p-3 text-sm">
              <p className="text-slate-400 mb-1">Best for:</p>
              <ul className="list-disc list-inside text-slate-300 space-y-1">
                <li>Cloud-hosted databases (AWS RDS, Azure SQL, Google Cloud SQL)</li>
                <li>Databases with public IP addresses</li>
                <li>VPN-connected networks</li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500/10 to-purple-500/5 rounded-lg border border-purple-500/30 p-4">
            <h4 className="text-purple-400 font-semibold mb-2 flex items-center space-x-2">
              <span>🔐</span>
              <span>SSH Tunnel (Recommended for On-Premises)</span>
            </h4>
            <p className="text-slate-300 text-sm mb-3">
              Most secure method. Creates an encrypted tunnel through an SSH server to reach your database.
            </p>
            <div className="bg-slate-900 rounded p-3 text-sm">
              <p className="text-slate-400 mb-1">Best for:</p>
              <ul className="list-disc list-inside text-slate-300 space-y-1">
                <li>On-premises databases behind firewalls</li>
                <li>Databases without public IP addresses</li>
                <li>Maximum security requirements</li>
                <li>School/institution internal networks</li>
              </ul>
            </div>
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <p className="text-yellow-300 text-sm">
              🔒 <strong>Security Note:</strong> SSH Tunnel is recommended for on-premises databases as it provides end-to-end encryption and doesn't require exposing your database to the internet.
            </p>
          </div>
        </div>
      </div>

      {/* Step 4: Configure Connection Details */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="bg-pink-500/20 rounded-full w-8 h-8 flex items-center justify-center">
            <span className="text-pink-400 font-bold">4</span>
          </div>
          <h2 className="text-2xl font-bold text-white">Configure Connection Details</h2>
        </div>

        <div className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Basic Information</h3>
            <div className="space-y-3">
              <div className="bg-slate-900 rounded-lg p-4">
                <label className="block text-sm font-medium text-slate-400 mb-2">Connection Name *</label>
                <input 
                  type="text" 
                  placeholder="School District Database"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  disabled
                />
                <p className="text-xs text-slate-500 mt-1">Give your connection a memorable name</p>
              </div>

              <div className="bg-slate-900 rounded-lg p-4">
                <label className="block text-sm font-medium text-slate-400 mb-2">Description (Optional)</label>
                <input 
                  type="text" 
                  placeholder="Main student database for District A"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  disabled
                />
              </div>
            </div>
          </div>

          {/* Database Credentials */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Database Credentials</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-900 rounded-lg p-4">
                <label className="block text-sm font-medium text-slate-400 mb-2">Host *</label>
                <input 
                  type="text" 
                  placeholder="db.school.edu or 192.168.1.100"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  disabled
                />
              </div>

              <div className="bg-slate-900 rounded-lg p-4">
                <label className="block text-sm font-medium text-slate-400 mb-2">Port *</label>
                <input 
                  type="text" 
                  placeholder="5432"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  disabled
                />
              </div>

              <div className="bg-slate-900 rounded-lg p-4">
                <label className="block text-sm font-medium text-slate-400 mb-2">Database Name *</label>
                <input 
                  type="text" 
                  placeholder="students_db"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  disabled
                />
              </div>

              <div className="bg-slate-900 rounded-lg p-4">
                <label className="block text-sm font-medium text-slate-400 mb-2">Username *</label>
                <input 
                  type="text" 
                  placeholder="readonly_user"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  disabled
                />
              </div>

              <div className="bg-slate-900 rounded-lg p-4 col-span-2">
                <label className="block text-sm font-medium text-slate-400 mb-2">Password *</label>
                <input 
                  type="password" 
                  placeholder="••••••••"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  disabled
                />
                <p className="text-xs text-slate-500 mt-1">🔒 Encrypted with bcrypt before storage</p>
              </div>
            </div>
          </div>

          {/* SSH Tunnel Configuration */}
          <div className="border-t border-slate-700 pt-4">
            <h3 className="text-lg font-semibold text-white mb-3">SSH Tunnel Configuration (If Selected)</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-900 rounded-lg p-4">
                  <label className="block text-sm font-medium text-slate-400 mb-2">SSH Host *</label>
                  <input 
                    type="text" 
                    placeholder="ssh.school.edu"
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    disabled
                  />
                </div>

                <div className="bg-slate-900 rounded-lg p-4">
                  <label className="block text-sm font-medium text-slate-400 mb-2">SSH Port</label>
                  <input 
                    type="text" 
                    placeholder="22"
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    disabled
                  />
                </div>
              </div>

              <div className="bg-slate-900 rounded-lg p-4">
                <label className="block text-sm font-medium text-slate-400 mb-2">SSH Username *</label>
                <input 
                  type="text" 
                  placeholder="admin"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  disabled
                />
              </div>

              <div className="bg-slate-900 rounded-lg p-4">
                <label className="block text-sm font-medium text-slate-400 mb-2">SSH Private Key *</label>
                <div className="flex items-center space-x-3">
                  <button className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm">
                    Upload Key File
                  </button>
                  <span className="text-sm text-slate-400">.pem or .key file</span>
                </div>
                <p className="text-xs text-slate-500 mt-2">🔐 Stored securely in Vercel KV</p>
              </div>
            </div>
          </div>

          {/* Options */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Security Options</h3>
            <div className="space-y-2">
              <label className="flex items-center space-x-3 p-3 bg-slate-900 rounded-lg cursor-pointer">
                <input type="checkbox" checked disabled className="w-4 h-4" />
                <div>
                  <span className="text-white font-medium">Use SSL/TLS</span>
                  <p className="text-xs text-slate-400">Encrypt connection to database</p>
                </div>
              </label>

              <label className="flex items-center space-x-3 p-3 bg-slate-900 rounded-lg cursor-pointer">
                <input type="checkbox" checked disabled className="w-4 h-4" />
                <div>
                  <span className="text-white font-medium">Read-only mode (Recommended)</span>
                  <p className="text-xs text-slate-400">Prevents accidental data modification</p>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Step 5: Test Connection */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="bg-yellow-500/20 rounded-full w-8 h-8 flex items-center justify-center">
            <span className="text-yellow-400 font-bold">5</span>
          </div>
          <h2 className="text-2xl font-bold text-white">Test Your Connection</h2>
        </div>

        <p className="text-slate-300 mb-4">
          Before saving, the wizard will test your connection to ensure everything is configured correctly.
        </p>

        <div className="space-y-4">
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-6 h-6 text-green-400 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-green-400 font-semibold mb-2">Connection Successful!</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Latency:</span>
                    <span className="text-white font-mono">45ms</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Database:</span>
                    <span className="text-white font-mono">students_db</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Version:</span>
                    <span className="text-white font-mono text-xs">PostgreSQL 14.5</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-slate-700/30 rounded-lg p-4">
            <p className="text-slate-300 text-sm">
              ✅ <strong>What's being tested:</strong>
            </p>
            <ul className="list-disc list-inside text-slate-400 text-sm space-y-1 mt-2 ml-4">
              <li>Network connectivity to database host</li>
              <li>SSH tunnel establishment (if configured)</li>
              <li>Database authentication</li>
              <li>Permission to read database schema</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Example Connections */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Example Connections</h2>

        <div className="space-y-4">
          {/* PostgreSQL Cloud Example */}
          <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
            <h4 className="text-blue-400 font-semibold mb-3 flex items-center space-x-2">
              <span>🐘</span>
              <span>PostgreSQL on AWS RDS (Direct Connection)</span>
            </h4>
            <div className="bg-slate-800 rounded p-3 font-mono text-sm space-y-1">
              <div><span className="text-slate-500">Host:</span> <span className="text-green-400">mydb.abc123.us-east-1.rds.amazonaws.com</span></div>
              <div><span className="text-slate-500">Port:</span> <span className="text-green-400">5432</span></div>
              <div><span className="text-slate-500">Database:</span> <span className="text-green-400">school_students</span></div>
              <div><span className="text-slate-500">Username:</span> <span className="text-green-400">sri_readonly</span></div>
              <div><span className="text-slate-500">SSL:</span> <span className="text-green-400">Enabled</span></div>
            </div>
          </div>

          {/* MySQL with SSH Example */}
          <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
            <h4 className="text-green-400 font-semibold mb-3 flex items-center space-x-2">
              <span>🐬</span>
              <span>MySQL On-Premises (SSH Tunnel)</span>
            </h4>
            <div className="bg-slate-800 rounded p-3 font-mono text-sm space-y-1">
              <div><span className="text-slate-500">SSH Host:</span> <span className="text-purple-400">ssh.school.edu</span></div>
              <div><span className="text-slate-500">SSH Port:</span> <span className="text-purple-400">22</span></div>
              <div><span className="text-slate-500">SSH User:</span> <span className="text-purple-400">admin</span></div>
              <div className="border-t border-slate-700 my-2"></div>
              <div><span className="text-slate-500">DB Host:</span> <span className="text-green-400">localhost</span> <span className="text-slate-600">(internal)</span></div>
              <div><span className="text-slate-500">DB Port:</span> <span className="text-green-400">3306</span></div>
              <div><span className="text-slate-500">Database:</span> <span className="text-green-400">athletics_db</span></div>
            </div>
          </div>

          {/* SQL Server Example */}
          <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
            <h4 className="text-purple-400 font-semibold mb-3 flex items-center space-x-2">
              <span>🗄️</span>
              <span>SQL Server (Direct Connection)</span>
            </h4>
            <div className="bg-slate-800 rounded p-3 font-mono text-sm space-y-1">
              <div><span className="text-slate-500">Host:</span> <span className="text-green-400">sqlserver.district.local</span></div>
              <div><span className="text-slate-500">Port:</span> <span className="text-green-400">1433</span></div>
              <div><span className="text-slate-500">Database:</span> <span className="text-green-400">StudentRecords</span></div>
              <div><span className="text-slate-500">Username:</span> <span className="text-green-400">sri_reader</span></div>
              <div><span className="text-slate-500">Encryption:</span> <span className="text-green-400">Enabled</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* Security Best Practices */}
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
        <h2 className="text-2xl font-bold text-red-400 mb-4 flex items-center space-x-2">
          <Shield className="w-6 h-6" />
          <span>Security Best Practices</span>
        </h2>

        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
            <div>
              <p className="text-white font-medium">Create a Read-Only Database User</p>
              <p className="text-sm text-slate-400">Never use admin credentials. Create a dedicated user with SELECT-only permissions.</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
            <div>
              <p className="text-white font-medium">Use SSH Tunnels for On-Premises Databases</p>
              <p className="text-sm text-slate-400">Don't expose your database to the internet. Use SSH tunneling for secure access.</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
            <div>
              <p className="text-white font-medium">Enable SSL/TLS Encryption</p>
              <p className="text-sm text-slate-400">Always encrypt data in transit between SRI and your database.</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
            <div>
              <p className="text-white font-medium">Restrict Network Access</p>
              <p className="text-sm text-slate-400">Configure firewall rules to only allow connections from SRI's IP addresses.</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
            <div>
              <p className="text-white font-medium">Monitor Connection Activity</p>
              <p className="text-sm text-slate-400">Review audit logs regularly to track all database access and queries.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Troubleshooting */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Troubleshooting Common Issues</h2>

        <div className="space-y-4">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-red-400 font-semibold mb-2">❌ Connection Timeout</h4>
            <p className="text-slate-300 text-sm mb-2">Possible causes:</p>
            <ul className="list-disc list-inside text-slate-400 text-sm space-y-1 ml-4">
              <li>Firewall blocking connection</li>
              <li>Incorrect host or port</li>
              <li>Database server not running</li>
            </ul>
            <p className="text-green-400 text-sm mt-2">✓ Solution: Check firewall rules and verify database is accessible</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-red-400 font-semibold mb-2">❌ Authentication Failed</h4>
            <p className="text-slate-300 text-sm mb-2">Possible causes:</p>
            <ul className="list-disc list-inside text-slate-400 text-sm space-y-1 ml-4">
              <li>Incorrect username or password</li>
              <li>User doesn't have remote access permissions</li>
              <li>Database requires SSL but not enabled</li>
            </ul>
            <p className="text-green-400 text-sm mt-2">✓ Solution: Verify credentials and check user permissions</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-red-400 font-semibold mb-2">❌ SSH Tunnel Failed</h4>
            <p className="text-slate-300 text-sm mb-2">Possible causes:</p>
            <ul className="list-disc list-inside text-slate-400 text-sm space-y-1 ml-4">
              <li>Invalid SSH private key</li>
              <li>SSH server not accessible</li>
              <li>SSH user doesn't have tunnel permissions</li>
            </ul>
            <p className="text-green-400 text-sm mt-2">✓ Solution: Verify SSH key format and server accessibility</p>
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/30 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">After Connecting</h2>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-500/20 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
              <span className="text-blue-400">1</span>
            </div>
            <p className="text-slate-300">Your connection will appear in the External DB → Live Connections tab</p>
          </div>

          <div className="flex items-center space-x-3">
            <div className="bg-green-500/20 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
              <span className="text-green-400">2</span>
            </div>
            <p className="text-slate-300">Go to Manual Explorer and select your database from the Data Source dropdown</p>
          </div>

          <div className="flex items-center space-x-3">
            <div className="bg-purple-500/20 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
              <span className="text-purple-400">3</span>
            </div>
            <p className="text-slate-300">Start querying and analyzing your data with SRI's powerful analytics tools</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function LiveConnectionsContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Live Database Connections</h1>
      <p className="text-lg text-slate-400 mb-6">
        Real-time connectivity to external SQL databases
      </p>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Supported Databases</h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-slate-900 rounded-lg p-4 text-center">
            <div className="text-4xl mb-2">🐘</div>
            <h4 className="text-white font-semibold mb-1">PostgreSQL</h4>
            <p className="text-xs text-slate-400">Version 9.6+</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4 text-center">
            <div className="text-4xl mb-2">🐬</div>
            <h4 className="text-white font-semibold mb-1">MySQL</h4>
            <p className="text-xs text-slate-400">Version 5.7+</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4 text-center">
            <div className="text-4xl mb-2">🗄️</div>
            <h4 className="text-white font-semibold mb-1">SQL Server</h4>
            <p className="text-xs text-slate-400">Version 2016+</p>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Connection Features</h2>
        <div className="space-y-3">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">Connection Testing</h4>
            <p className="text-sm text-slate-400">Test connectivity before saving - shows latency and database version</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-2">SSH Tunnel Support</h4>
            <p className="text-sm text-slate-400">Secure access to on-premises databases through encrypted SSH tunnels</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">SSL/TLS Encryption</h4>
            <p className="text-sm text-slate-400">All database connections encrypted in transit</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-pink-400 font-semibold mb-2">Read-Only Mode</h4>
            <p className="text-sm text-slate-400">Prevents accidental data modification - SELECT queries only</p>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Management</h2>
        <p className="text-slate-300 mb-4">From the External DB → Live Connections tab:</p>
        <div className="space-y-2 text-sm text-slate-300">
          <p>• <strong>View All Connections:</strong> See list of configured databases</p>
          <p>• <strong>Test Connection:</strong> Verify connectivity at any time</p>
          <p>• <strong>Edit Connection:</strong> Update credentials or settings</p>
          <p>• <strong>Delete Connection:</strong> Remove connection (data not affected)</p>
          <p>• <strong>View Audit Log:</strong> Track all connection activity</p>
        </div>
      </div>

      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
        <h3 className="text-yellow-400 font-semibold mb-3">⚠️ Important Notes</h3>
        <ul className="space-y-2 text-slate-300 text-sm">
          <li>• Credentials are encrypted with bcrypt before storage</li>
          <li>• SSH private keys stored securely in Vercel KV</li>
          <li>• All queries are logged for audit compliance</li>
          <li>• Connection timeouts set to 30 seconds</li>
        </ul>
      </div>
    </div>
  );
}

function DataImportsContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">CSV/Excel Data Imports</h1>
      <p className="text-lg text-slate-400 mb-6">
        Upload and import athlete data from files
      </p>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Supported File Formats</h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-green-500/10 to-blue-500/10 rounded-lg border border-green-500/30 p-4">
            <div className="text-3xl mb-2">📄</div>
            <h4 className="text-white font-semibold mb-1">CSV</h4>
            <p className="text-xs text-slate-400">Comma-separated values</p>
          </div>

          <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/30 p-4">
            <div className="text-3xl mb-2">📗</div>
            <h4 className="text-white font-semibold mb-1">Excel</h4>
            <p className="text-xs text-slate-400">.xlsx, .xls files</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-lg border border-purple-500/30 p-4">
            <div className="text-3xl mb-2">📦</div>
            <h4 className="text-white font-semibold mb-1">JSON</h4>
            <p className="text-xs text-slate-400">Structured data format</p>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Import Wizard (4 Steps)</h2>
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <div className="bg-blue-500/20 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-blue-400 font-bold">1</span>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-1">Upload File</h4>
              <p className="text-slate-400 text-sm">Drag & drop or select file (max 10MB)</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="bg-green-500/20 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-green-400 font-bold">2</span>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-1">Schema Validation</h4>
              <p className="text-slate-400 text-sm">Automatic column mapping with fuzzy matching</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="bg-purple-500/20 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-purple-400 font-bold">3</span>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-1">Review & Configure</h4>
              <p className="text-slate-400 text-sm">Set dataset name, description, and review mapping</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="bg-pink-500/20 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-pink-400 font-bold">4</span>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-1">Import & Track Progress</h4>
              <p className="text-slate-400 text-sm">Real-time progress bar shows import status</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Smart Features</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">Fuzzy Column Matching</h4>
            <p className="text-sm text-slate-400">Automatically maps columns like "height" to "height_cm"</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-2">Data Validation</h4>
            <p className="text-sm text-slate-400">Checks data types, ranges, and required fields</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">Error Reporting</h4>
            <p className="text-sm text-slate-400">Shows which rows have validation errors and why</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-pink-400 font-semibold mb-2">Separate Tables</h4>
            <p className="text-sm text-slate-400">Each import stored in unique table (athletes_import_uuid)</p>
          </div>
        </div>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
        <h3 className="text-blue-400 font-semibold mb-3">After Import</h3>
        <p className="text-slate-300 mb-3">Your imported dataset:</p>
        <ul className="space-y-2 text-slate-300 text-sm">
          <li>• Appears in Manual Explorer data source dropdown</li>
          <li>• Shows record count and validation status</li>
          <li>• Can be queried like any other data source</li>
          <li>• Can be archived or deleted at any time</li>
        </ul>
      </div>
    </div>
  );
}

function DatabaseSchemaContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Database Schema Reference</h1>
      <p className="text-lg text-slate-400 mb-6">
        Complete reference for the SRI Platform database schema
      </p>

      <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/30 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Schema Overview</h2>
        <p className="text-slate-300 mb-4">
          The SRI Platform uses PostgreSQL with a normalized relational schema. All tables use UUID primary keys and include audit timestamps.
        </p>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Core Tables</h2>
        
        <div className="space-y-6">
          {/* Athletes Table */}
          <div className="bg-slate-900 rounded-lg p-4">
            <h3 className="text-blue-400 font-bold text-lg mb-3">athletes</h3>
            <p className="text-sm text-slate-400 mb-3">Primary table storing athlete profiles and assessment data</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-2 text-slate-400 font-semibold">Column</th>
                    <th className="text-left py-2 text-slate-400 font-semibold">Type</th>
                    <th className="text-left py-2 text-slate-400 font-semibold">Description</th>
                  </tr>
                </thead>
                <tbody className="text-slate-300">
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">id</code></td>
                    <td className="py-2">UUID</td>
                    <td className="py-2">Primary key</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">student_id</code></td>
                    <td className="py-2">VARCHAR</td>
                    <td className="py-2">Unique student identifier</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">first_name</code></td>
                    <td className="py-2">VARCHAR</td>
                    <td className="py-2">Athlete's first name</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">last_name</code></td>
                    <td className="py-2">VARCHAR</td>
                    <td className="py-2">Athlete's last name</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">gender</code></td>
                    <td className="py-2">VARCHAR</td>
                    <td className="py-2">'Male', 'Female', or 'Other'</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">date_of_birth</code></td>
                    <td className="py-2">DATE</td>
                    <td className="py-2">Birth date</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">district_id</code></td>
                    <td className="py-2">UUID</td>
                    <td className="py-2">FK to districts table</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">town_id</code></td>
                    <td className="py-2">UUID</td>
                    <td className="py-2">FK to towns table</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">school_id</code></td>
                    <td className="py-2">UUID</td>
                    <td className="py-2">FK to schools table</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">height_cm</code></td>
                    <td className="py-2">DECIMAL</td>
                    <td className="py-2">Height in centimeters</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">weight_kg</code></td>
                    <td className="py-2">DECIMAL</td>
                    <td className="py-2">Weight in kilograms</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">wingspan_cm</code></td>
                    <td className="py-2">DECIMAL</td>
                    <td className="py-2">Wingspan in centimeters</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">vertical_jump_cm</code></td>
                    <td className="py-2">DECIMAL</td>
                    <td className="py-2">Vertical jump height</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">sprint_30m_sec</code></td>
                    <td className="py-2">DECIMAL</td>
                    <td className="py-2">30m sprint time</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">sprint_100m_sec</code></td>
                    <td className="py-2">DECIMAL</td>
                    <td className="py-2">100m sprint time</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">broad_jump_cm</code></td>
                    <td className="py-2">DECIMAL</td>
                    <td className="py-2">Broad jump distance</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">ape_index</code></td>
                    <td className="py-2">DECIMAL</td>
                    <td className="py-2">Wingspan - Height (generated)</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">bmi</code></td>
                    <td className="py-2">DECIMAL</td>
                    <td className="py-2">Body Mass Index (generated)</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code className="text-green-400">created_at</code></td>
                    <td className="py-2">TIMESTAMP</td>
                    <td className="py-2">Record creation timestamp</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Districts Table */}
          <div className="bg-slate-900 rounded-lg p-4">
            <h3 className="text-green-400 font-bold text-lg mb-3">districts</h3>
            <p className="text-sm text-slate-400 mb-3">Administrative districts</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-2 text-slate-400 font-semibold">Column</th>
                    <th className="text-left py-2 text-slate-400 font-semibold">Type</th>
                    <th className="text-left py-2 text-slate-400 font-semibold">Description</th>
                  </tr>
                </thead>
                <tbody className="text-slate-300">
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">id</code></td>
                    <td className="py-2">UUID</td>
                    <td className="py-2">Primary key</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">name</code></td>
                    <td className="py-2">TEXT</td>
                    <td className="py-2">District name (e.g., 'Patna', 'Gaya')</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code className="text-green-400">population</code></td>
                    <td className="py-2">INTEGER</td>
                    <td className="py-2">District population</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Towns Table */}
          <div className="bg-slate-900 rounded-lg p-4">
            <h3 className="text-purple-400 font-bold text-lg mb-3">towns</h3>
            <p className="text-sm text-slate-400 mb-3">Towns within districts</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-2 text-slate-400 font-semibold">Column</th>
                    <th className="text-left py-2 text-slate-400 font-semibold">Type</th>
                    <th className="text-left py-2 text-slate-400 font-semibold">Description</th>
                  </tr>
                </thead>
                <tbody className="text-slate-300">
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">id</code></td>
                    <td className="py-2">UUID</td>
                    <td className="py-2">Primary key</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">name</code></td>
                    <td className="py-2">TEXT</td>
                    <td className="py-2">Town name</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">district_id</code></td>
                    <td className="py-2">UUID</td>
                    <td className="py-2">FK to districts</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code className="text-green-400">type</code></td>
                    <td className="py-2">TEXT</td>
                    <td className="py-2">'urban', 'semi-urban', or 'rural'</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Schools Table */}
          <div className="bg-slate-900 rounded-lg p-4">
            <h3 className="text-pink-400 font-bold text-lg mb-3">schools</h3>
            <p className="text-sm text-slate-400 mb-3">Educational institutions</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-2 text-slate-400 font-semibold">Column</th>
                    <th className="text-left py-2 text-slate-400 font-semibold">Type</th>
                    <th className="text-left py-2 text-slate-400 font-semibold">Description</th>
                  </tr>
                </thead>
                <tbody className="text-slate-300">
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">id</code></td>
                    <td className="py-2">UUID</td>
                    <td className="py-2">Primary key</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">name</code></td>
                    <td className="py-2">TEXT</td>
                    <td className="py-2">School name</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-2"><code className="text-green-400">town_id</code></td>
                    <td className="py-2">UUID</td>
                    <td className="py-2">FK to towns</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code className="text-green-400">type</code></td>
                    <td className="py-2">TEXT</td>
                    <td className="py-2">'government', 'private', or 'aided'</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Relationships</h2>
        <div className="space-y-3">
          <div className="bg-slate-900 rounded-lg p-4">
            <code className="text-sm text-slate-300">
              athletes.district_id → districts.id (Many-to-One)
            </code>
          </div>
          <div className="bg-slate-900 rounded-lg p-4">
            <code className="text-sm text-slate-300">
              athletes.town_id → towns.id (Many-to-One)
            </code>
          </div>
          <div className="bg-slate-900 rounded-lg p-4">
            <code className="text-sm text-slate-300">
              athletes.school_id → schools.id (Many-to-One)
            </code>
          </div>
          <div className="bg-slate-900 rounded-lg p-4">
            <code className="text-sm text-slate-300">
              towns.district_id → districts.id (Many-to-One)
            </code>
          </div>
          <div className="bg-slate-900 rounded-lg p-4">
            <code className="text-sm text-slate-300">
              schools.town_id → towns.id (Many-to-One)
            </code>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Computed Columns</h2>
        <div className="space-y-3">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">ape_index</h4>
            <code className="text-sm text-green-400">wingspan_cm - height_cm</code>
            <p className="text-sm text-slate-400 mt-2">Indicates arm span relative to height (important for sports like basketball, boxing)</p>
          </div>
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">bmi</h4>
            <code className="text-sm text-green-400">weight_kg / (height_cm / 100)²</code>
            <p className="text-sm text-slate-400 mt-2">Body Mass Index for health assessment</p>
          </div>
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-pink-400 font-semibold mb-2">age</h4>
            <code className="text-sm text-green-400">EXTRACT(YEAR FROM AGE(CURRENT_DATE, date_of_birth))</code>
            <p className="text-sm text-slate-400 mt-2">Calculated age in years</p>
          </div>
        </div>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
        <h3 className="text-blue-400 font-semibold mb-3">Example Query</h3>
        <div className="bg-slate-900 rounded-lg p-4">
          <pre className="text-xs text-slate-300 overflow-x-auto">
{`SELECT 
  a.first_name || ' ' || a.last_name AS athlete_name,
  a.gender,
  EXTRACT(YEAR FROM AGE(CURRENT_DATE, a.date_of_birth)) AS age,
  a.height_cm,
  a.weight_kg,
  a.ape_index,
  d.name AS district,
  t.name AS town,
  s.name AS school
FROM athletes a
LEFT JOIN districts d ON a.district_id = d.id
LEFT JOIN towns t ON a.town_id = t.id
LEFT JOIN schools s ON a.school_id = s.id
WHERE a.height_cm > 170
ORDER BY a.height_cm DESC
LIMIT 10;`}
          </pre>
        </div>
      </div>
    </div>
  );
}

function SchemaMappingContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Schema Mapping</h1>
      <p className="text-lg text-slate-400 mb-6">
        Automatic and manual column mapping for data imports
      </p>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">SRI Schema</h2>
        <p className="text-slate-300 mb-4">
          All athlete data must conform to the SRI schema. The system automatically maps your columns to SRI fields.
        </p>
        <div className="bg-slate-900 rounded-lg p-4">
          <p className="text-sm text-slate-400 mb-2">Required fields:</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <code className="text-green-400">first_name</code>
            <code className="text-green-400">last_name</code>
            <code className="text-green-400">gender</code>
            <code className="text-green-400">date_of_birth</code>
            <code className="text-green-400">height_cm</code>
            <code className="text-green-400">weight_kg</code>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Fuzzy Matching Algorithm</h2>
        <p className="text-slate-300 mb-4">
          Uses Levenshtein distance to match similar column names:
        </p>
        <div className="space-y-2">
          <div className="bg-slate-900 rounded-lg p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">Your column:</span>
              <code className="text-blue-400">Height (cm)</code>
              <span className="text-slate-600">→</span>
              <code className="text-green-400">height_cm</code>
              <span className="text-slate-400">:SRI field</span>
            </div>
          </div>

          <div className="bg-slate-900 rounded-lg p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">Your column:</span>
              <code className="text-blue-400">FirstName</code>
              <span className="text-slate-600">→</span>
              <code className="text-green-400">first_name</code>
              <span className="text-slate-400">:SRI field</span>
            </div>
          </div>

          <div className="bg-slate-900 rounded-lg p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">Your column:</span>
              <code className="text-blue-400">DOB</code>
              <span className="text-slate-600">→</span>
              <code className="text-green-400">date_of_birth</code>
              <span className="text-slate-400">:SRI field</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Validation Rules</h2>
        <div className="space-y-3">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">Data Types</h4>
            <p className="text-sm text-slate-400">Numbers must be numeric, dates must be valid ISO format</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-2">Value Ranges</h4>
            <p className="text-sm text-slate-400">Height: 50-250cm, Weight: 10-200kg, Age: 5-25 years</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">Enum Values</h4>
            <p className="text-sm text-slate-400">Gender must be 'Male' or 'Female'</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-pink-400 font-semibold mb-2">Required Fields</h4>
            <p className="text-sm text-slate-400">Name, gender, DOB, height, weight cannot be null</p>
          </div>
        </div>
      </div>

      <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6">
        <h3 className="text-green-400 font-semibold mb-3">Validation Results</h3>
        <p className="text-slate-300 mb-3">After validation, you'll see:</p>
        <ul className="space-y-2 text-slate-300 text-sm">
          <li>• <strong className="text-green-400">Valid records:</strong> Ready to import</li>
          <li>• <strong className="text-yellow-400">Warnings:</strong> Optional fields missing</li>
          <li>• <strong className="text-red-400">Errors:</strong> Required fields missing or invalid</li>
          <li>• <strong>Error details:</strong> Which rows and what's wrong</li>
        </ul>
      </div>
    </div>
  );
}

function AuthenticationContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Authentication & Authorization</h1>
      <p className="text-lg text-slate-400 mb-6">
        Enterprise-grade security powered by Stack Auth
      </p>

      <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/30 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Stack Auth Integration</h2>
        <p className="text-slate-300 mb-4">
          SRI Platform uses Stack Auth for authentication and authorization, providing secure, scalable user management with support for multiple authentication methods.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">🔐 OAuth 2.0</h4>
            <p className="text-sm text-slate-400">Industry-standard authentication protocol</p>
          </div>
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-2">🔑 JWT Tokens</h4>
            <p className="text-sm text-slate-400">Secure, stateless session management</p>
          </div>
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">📧 Email/Password</h4>
            <p className="text-sm text-slate-400">Traditional authentication method</p>
          </div>
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-pink-400 font-semibold mb-2">🔗 Social Login</h4>
            <p className="text-sm text-slate-400">Google, GitHub integration</p>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">User Roles & Permissions</h2>
        <div className="space-y-3">
          <div className="bg-gradient-to-r from-red-500/10 to-red-500/5 rounded-lg border border-red-500/30 p-4">
            <h4 className="text-red-400 font-semibold mb-2">👑 Super Admin</h4>
            <p className="text-sm text-slate-400 mb-2">Full system access and control</p>
            <ul className="text-xs text-slate-400 space-y-1 ml-4">
              <li>• Manage all users and roles</li>
              <li>• Configure system settings</li>
              <li>• Access all data sources</li>
              <li>• View audit logs</li>
            </ul>
          </div>

          <div className="bg-gradient-to-r from-blue-500/10 to-blue-500/5 rounded-lg border border-blue-500/30 p-4">
            <h4 className="text-blue-400 font-semibold mb-2">👨‍💼 Admin</h4>
            <p className="text-sm text-slate-400 mb-2">Organization-level management</p>
            <ul className="text-xs text-slate-400 space-y-1 ml-4">
              <li>• Manage athletes and assessments</li>
              <li>• Configure external connections</li>
              <li>• Send Splink invitations</li>
              <li>• Export reports</li>
            </ul>
          </div>

          <div className="bg-gradient-to-r from-green-500/10 to-green-500/5 rounded-lg border border-green-500/30 p-4">
            <h4 className="text-green-400 font-semibold mb-2">👤 User (Athlete)</h4>
            <p className="text-sm text-slate-400 mb-2">Personal data access only</p>
            <ul className="text-xs text-slate-400 space-y-1 ml-4">
              <li>• View own assessment reports</li>
              <li>• Track personal progress</li>
              <li>• Update profile information</li>
              <li>• Download PDF reports</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Session Management</h2>
        <div className="space-y-3">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">Token Expiration</h4>
            <p className="text-sm text-slate-400">JWT tokens expire after 24 hours for security</p>
          </div>
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-2">Refresh Tokens</h4>
            <p className="text-sm text-slate-400">Automatic token refresh for seamless experience</p>
          </div>
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">Logout</h4>
            <p className="text-sm text-slate-400">Secure session termination and token invalidation</p>
          </div>
        </div>
      </div>

      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
        <h3 className="text-yellow-400 font-semibold mb-3 flex items-center space-x-2">
          <Shield className="w-5 h-5" />
          <span>Security Best Practices</span>
        </h3>
        <ul className="space-y-2 text-slate-300 text-sm">
          <li>• Passwords hashed with bcrypt (10 rounds)</li>
          <li>• HTTPS enforced for all connections</li>
          <li>• CSRF protection enabled</li>
          <li>• Rate limiting on authentication endpoints</li>
          <li>• Failed login attempt tracking</li>
        </ul>
      </div>
    </div>
  );
}

function DataEncryptionContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Data Encryption</h1>
      <p className="text-lg text-slate-400 mb-6">
        Multi-layer encryption for data at rest and in transit
      </p>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Encryption at Rest</h2>
        <div className="space-y-3">
          <div className="bg-gradient-to-r from-blue-500/10 to-blue-500/5 rounded-lg border border-blue-500/30 p-4">
            <h4 className="text-blue-400 font-semibold mb-2">🗄️ Database Encryption</h4>
            <p className="text-sm text-slate-400 mb-2">Neon PostgreSQL with AES-256 encryption</p>
            <ul className="text-xs text-slate-400 space-y-1 ml-4">
              <li>• All data encrypted at storage level</li>
              <li>• Automatic key rotation</li>
              <li>• Encrypted backups</li>
            </ul>
          </div>

          <div className="bg-gradient-to-r from-green-500/10 to-green-500/5 rounded-lg border border-green-500/30 p-4">
            <h4 className="text-green-400 font-semibold mb-2">🔑 Credential Encryption</h4>
            <p className="text-sm text-slate-400 mb-2">bcrypt hashing for passwords and sensitive data</p>
            <ul className="text-xs text-slate-400 space-y-1 ml-4">
              <li>• Passwords: bcrypt with 10 rounds</li>
              <li>• Database credentials: bcrypt encrypted</li>
              <li>• SSH keys: Stored in Vercel KV (encrypted)</li>
            </ul>
          </div>

          <div className="bg-gradient-to-r from-purple-500/10 to-purple-500/5 rounded-lg border border-purple-500/30 p-4">
            <h4 className="text-purple-400 font-semibold mb-2">📦 File Storage</h4>
            <p className="text-sm text-slate-400 mb-2">Vercel Blob storage with encryption</p>
            <ul className="text-xs text-slate-400 space-y-1 ml-4">
              <li>• Uploaded files encrypted</li>
              <li>• Temporary files auto-deleted</li>
              <li>• Access-controlled URLs</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Encryption in Transit</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">🔒 HTTPS/TLS 1.3</h4>
            <p className="text-sm text-slate-400">All web traffic encrypted with TLS 1.3</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-2">🔐 Database SSL</h4>
            <p className="text-sm text-slate-400">PostgreSQL connections use SSL/TLS</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">🚇 SSH Tunnels</h4>
            <p className="text-sm text-slate-400">External DB connections via encrypted SSH</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-pink-400 font-semibold mb-2">📡 API Calls</h4>
            <p className="text-sm text-slate-400">All API requests over HTTPS only</p>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Key Management</h2>
        <div className="space-y-3">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">Environment Variables</h4>
            <p className="text-sm text-slate-400">Sensitive keys stored in Vercel environment variables</p>
          </div>
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-2">Vercel KV</h4>
            <p className="text-sm text-slate-400">SSH private keys stored in encrypted Redis (Vercel KV)</p>
          </div>
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">No Hardcoded Secrets</h4>
            <p className="text-sm text-slate-400">Zero secrets in source code or version control</p>
          </div>
        </div>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
        <h3 className="text-blue-400 font-semibold mb-3">Compliance</h3>
        <ul className="space-y-2 text-slate-300 text-sm">
          <li>• GDPR compliant data handling</li>
          <li>• SOC 2 Type II infrastructure (Vercel, Neon)</li>
          <li>• Regular security audits</li>
          <li>• Data residency options available</li>
        </ul>
      </div>
    </div>
  );
}

function AccessControlContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Access Control</h1>
      <p className="text-lg text-slate-400 mb-6">
        Role-based access control (RBAC) and data isolation
      </p>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Permission Matrix</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left py-3 px-4 text-slate-400 font-semibold">Feature</th>
                <th className="text-center py-3 px-4 text-red-400 font-semibold">Super Admin</th>
                <th className="text-center py-3 px-4 text-blue-400 font-semibold">Admin</th>
                <th className="text-center py-3 px-4 text-green-400 font-semibold">User</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-b border-slate-700/50">
                <td className="py-3 px-4">View All Athletes</td>
                <td className="text-center">✅</td>
                <td className="text-center">✅</td>
                <td className="text-center">❌</td>
              </tr>
              <tr className="border-b border-slate-700/50">
                <td className="py-3 px-4">View Own Data</td>
                <td className="text-center">✅</td>
                <td className="text-center">✅</td>
                <td className="text-center">✅</td>
              </tr>
              <tr className="border-b border-slate-700/50">
                <td className="py-3 px-4">Add/Edit Athletes</td>
                <td className="text-center">✅</td>
                <td className="text-center">✅</td>
                <td className="text-center">❌</td>
              </tr>
              <tr className="border-b border-slate-700/50">
                <td className="py-3 px-4">External DB Connections</td>
                <td className="text-center">✅</td>
                <td className="text-center">✅</td>
                <td className="text-center">❌</td>
              </tr>
              <tr className="border-b border-slate-700/50">
                <td className="py-3 px-4">Data Import</td>
                <td className="text-center">✅</td>
                <td className="text-center">✅</td>
                <td className="text-center">❌</td>
              </tr>
              <tr className="border-b border-slate-700/50">
                <td className="py-3 px-4">Send Splink Invitations</td>
                <td className="text-center">✅</td>
                <td className="text-center">✅</td>
                <td className="text-center">❌</td>
              </tr>
              <tr className="border-b border-slate-700/50">
                <td className="py-3 px-4">User Management</td>
                <td className="text-center">✅</td>
                <td className="text-center">❌</td>
                <td className="text-center">❌</td>
              </tr>
              <tr className="border-b border-slate-700/50">
                <td className="py-3 px-4">View Audit Logs</td>
                <td className="text-center">✅</td>
                <td className="text-center">❌</td>
                <td className="text-center">❌</td>
              </tr>
              <tr>
                <td className="py-3 px-4">System Configuration</td>
                <td className="text-center">✅</td>
                <td className="text-center">❌</td>
                <td className="text-center">❌</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Data Isolation</h2>
        <div className="space-y-3">
          <div className="bg-gradient-to-r from-blue-500/10 to-blue-500/5 rounded-lg border border-blue-500/30 p-4">
            <h4 className="text-blue-400 font-semibold mb-2">🏢 Organization-Level Isolation</h4>
            <p className="text-sm text-slate-400">Each organization's data is logically separated with user_id filtering</p>
          </div>

          <div className="bg-gradient-to-r from-green-500/10 to-green-500/5 rounded-lg border border-green-500/30 p-4">
            <h4 className="text-green-400 font-semibold mb-2">👤 User-Level Isolation</h4>
            <p className="text-sm text-slate-400">Athletes can only access their own Splink ID data</p>
          </div>

          <div className="bg-gradient-to-r from-purple-500/10 to-purple-500/5 rounded-lg border border-purple-500/30 p-4">
            <h4 className="text-purple-400 font-semibold mb-2">🗄️ Database-Level Isolation</h4>
            <p className="text-sm text-slate-400">Imported datasets stored in separate tables per import</p>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">API Security</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">JWT Validation</h4>
            <p className="text-sm text-slate-400">All API requests require valid JWT token</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-2">Role Checking</h4>
            <p className="text-sm text-slate-400">Endpoint-level role verification</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">Rate Limiting</h4>
            <p className="text-sm text-slate-400">Prevents abuse and DDoS attacks</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-pink-400 font-semibold mb-2">Input Validation</h4>
            <p className="text-sm text-slate-400">SQL injection and XSS prevention</p>
          </div>
        </div>
      </div>

      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
        <h3 className="text-yellow-400 font-semibold mb-3">⚠️ Security Policies</h3>
        <ul className="space-y-2 text-slate-300 text-sm">
          <li>• Principle of least privilege enforced</li>
          <li>• Regular permission audits</li>
          <li>• Automatic session timeout after inactivity</li>
          <li>• Failed access attempts logged</li>
          <li>• Suspicious activity alerts</li>
        </ul>
      </div>
    </div>
  );
}

function AuditLoggingContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Audit Logging</h1>
      <p className="text-lg text-slate-400 mb-6">
        Comprehensive activity tracking and compliance logging
      </p>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">What Gets Logged</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/30 p-4">
            <h4 className="text-blue-400 font-semibold mb-2">🔐 Authentication Events</h4>
            <ul className="text-xs text-slate-400 space-y-1">
              <li>• User login/logout</li>
              <li>• Failed login attempts</li>
              <li>• Password changes</li>
              <li>• Session creation/termination</li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-green-500/10 to-blue-500/10 rounded-lg border border-green-500/30 p-4">
            <h4 className="text-green-400 font-semibold mb-2">📊 Data Access</h4>
            <ul className="text-xs text-slate-400 space-y-1">
              <li>• Database queries executed</li>
              <li>• Records viewed/exported</li>
              <li>• Report generation</li>
              <li>• Data source switches</li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-lg border border-purple-500/30 p-4">
            <h4 className="text-purple-400 font-semibold mb-2">✏️ Data Modifications</h4>
            <ul className="text-xs text-slate-400 space-y-1">
              <li>• Athlete records created/updated</li>
              <li>• Assessment data changes</li>
              <li>• User profile updates</li>
              <li>• Configuration changes</li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-pink-500/10 to-red-500/10 rounded-lg border border-pink-500/30 p-4">
            <h4 className="text-pink-400 font-semibold mb-2">🔌 External Connections</h4>
            <ul className="text-xs text-slate-400 space-y-1">
              <li>• Connection creation/deletion</li>
              <li>• Connection test attempts</li>
              <li>• Data import operations</li>
              <li>• External query execution</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Log Entry Structure</h2>
        <div className="bg-slate-900 rounded-lg p-4">
          <p className="text-sm text-slate-400 mb-3">Each log entry contains:</p>
          <div className="space-y-2 text-sm font-mono">
            <div className="flex justify-between">
              <span className="text-slate-500">timestamp:</span>
              <span className="text-green-400">"2025-12-24T18:30:45.123Z"</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">user_id:</span>
              <span className="text-green-400">"uuid-123-456"</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">action:</span>
              <span className="text-green-400">"external_db.create_connection"</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">resource:</span>
              <span className="text-green-400">"connection_id_789"</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">ip_address:</span>
              <span className="text-green-400">"192.168.1.100"</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">user_agent:</span>
              <span className="text-green-400">"Mozilla/5.0..."</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">status:</span>
              <span className="text-green-400">"success"</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">metadata:</span>
              <span className="text-green-400">{'{db_type: "postgresql"}'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Log Retention & Access</h2>
        <div className="space-y-3">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">📅 Retention Period</h4>
            <p className="text-sm text-slate-400">Logs retained for 90 days (configurable up to 1 year)</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-2">🔍 Search & Filter</h4>
            <p className="text-sm text-slate-400">Search by user, action, date range, or resource</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">📥 Export</h4>
            <p className="text-sm text-slate-400">Export logs to CSV for compliance reporting</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-pink-400 font-semibold mb-2">👑 Admin Only</h4>
            <p className="text-sm text-slate-400">Only Super Admins can view audit logs</p>
          </div>
        </div>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
        <h3 className="text-blue-400 font-semibold mb-3">Compliance Benefits</h3>
        <ul className="space-y-2 text-slate-300 text-sm">
          <li>• GDPR Article 30 compliance (records of processing activities)</li>
          <li>• SOC 2 audit trail requirements</li>
          <li>• HIPAA access logging (if applicable)</li>
          <li>• Forensic investigation support</li>
          <li>• Incident response documentation</li>
        </ul>
      </div>
    </div>
  );
}

function DataPrivacyContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Data Privacy & Compliance</h1>
      <p className="text-lg text-slate-400 mb-6">
        GDPR-compliant data handling and athlete privacy protection
      </p>

      <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/30 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Privacy Principles</h2>
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
            <div>
              <p className="text-white font-medium">Data Minimization</p>
              <p className="text-sm text-slate-400">Collect only necessary data for athlete assessment and development</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
            <div>
              <p className="text-white font-medium">Purpose Limitation</p>
              <p className="text-sm text-slate-400">Data used only for stated purposes (talent identification, performance tracking)</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
            <div>
              <p className="text-white font-medium">Data Accuracy</p>
              <p className="text-sm text-slate-400">Athletes can update their own information for accuracy</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
            <div>
              <p className="text-white font-medium">Storage Limitation</p>
              <p className="text-sm text-slate-400">Data retained only as long as necessary for stated purposes</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">GDPR Rights</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">📖 Right to Access</h4>
            <p className="text-sm text-slate-400">Athletes can view all their personal data via Splink dashboard</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-2">✏️ Right to Rectification</h4>
            <p className="text-sm text-slate-400">Athletes can update incorrect or incomplete information</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">🗑️ Right to Erasure</h4>
            <p className="text-sm text-slate-400">Athletes can request deletion of their data ("right to be forgotten")</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-pink-400 font-semibold mb-2">📥 Right to Portability</h4>
            <p className="text-sm text-slate-400">Export personal data in machine-readable format (JSON/CSV)</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-yellow-400 font-semibold mb-2">🚫 Right to Object</h4>
            <p className="text-sm text-slate-400">Athletes can object to certain data processing activities</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-red-400 font-semibold mb-2">⏸️ Right to Restriction</h4>
            <p className="text-sm text-slate-400">Request limitation of data processing in certain circumstances</p>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Consent Management</h2>
        <div className="space-y-3">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">Explicit Consent</h4>
            <p className="text-sm text-slate-400 mb-2">Clear consent obtained before data collection</p>
            <ul className="text-xs text-slate-400 space-y-1 ml-4">
              <li>• Assessment data collection consent</li>
              <li>• Splink ID creation consent</li>
              <li>• Email communication consent</li>
            </ul>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-2">Withdrawal of Consent</h4>
            <p className="text-sm text-slate-400">Athletes can withdraw consent at any time via account settings</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">Parental Consent</h4>
            <p className="text-sm text-slate-400">For athletes under 18, parental/guardian consent required</p>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Data Sharing & Third Parties</h2>
        <div className="space-y-3">
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-2">✅ No Data Selling</h4>
            <p className="text-sm text-slate-400">SRI never sells athlete data to third parties</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">Service Providers</h4>
            <p className="text-sm text-slate-400 mb-2">Limited data sharing with trusted providers:</p>
            <ul className="text-xs text-slate-400 space-y-1 ml-4">
              <li>• Vercel (hosting) - SOC 2 Type II certified</li>
              <li>• Neon (database) - SOC 2 Type II certified</li>
              <li>• Stack Auth (authentication) - GDPR compliant</li>
              <li>• Resend (email) - GDPR compliant</li>
            </ul>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">Data Processing Agreements</h4>
            <p className="text-sm text-slate-400">DPAs in place with all third-party processors</p>
          </div>
        </div>
      </div>

      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
        <h3 className="text-red-400 font-semibold mb-3 flex items-center space-x-2">
          <Shield className="w-5 h-5" />
          <span>Data Breach Protocol</span>
        </h3>
        <ul className="space-y-2 text-slate-300 text-sm">
          <li>• Breach detection and containment within 24 hours</li>
          <li>• Notification to affected users within 72 hours (GDPR requirement)</li>
          <li>• Notification to supervisory authority if required</li>
          <li>• Incident investigation and remediation</li>
          <li>• Post-incident security improvements</li>
        </ul>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
        <h3 className="text-blue-400 font-semibold mb-3">Contact Data Protection Officer</h3>
        <p className="text-slate-300 text-sm">For privacy concerns or data requests: <code className="text-green-400">privacy@sriplatform.com</code></p>
      </div>
    </div>
  );
}

function APIOverviewContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">API Documentation</h1>
      <p className="text-lg text-slate-400 mb-6">
        RESTful API for programmatic access to SRI Platform
      </p>

      <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/30 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Base URL</h2>
        <div className="bg-slate-900 rounded-lg p-4">
          <code className="text-green-400 text-lg">https://your-domain.vercel.app/api</code>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Authentication</h2>
        <p className="text-slate-300 mb-4">All API requests require authentication via JWT token in the Authorization header:</p>
        <div className="bg-slate-900 rounded-lg p-4">
          <code className="text-sm text-slate-400">Authorization: Bearer &lt;your_jwt_token&gt;</code>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Available Endpoints</h2>
        <div className="space-y-3">
          <div className="bg-slate-900 rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-2">
              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs font-mono">POST</span>
              <code className="text-green-400">/api</code>
            </div>
            <p className="text-sm text-slate-400">Main API endpoint - action-based routing</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-2">
              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs font-mono">POST</span>
              <code className="text-green-400">/api/external-db</code>
            </div>
            <p className="text-sm text-slate-400">External database connection management</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-2">
              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs font-mono">POST</span>
              <code className="text-green-400">/api/data-import</code>
            </div>
            <p className="text-sm text-slate-400">CSV/Excel data import operations</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-2">
              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs font-mono">POST</span>
              <code className="text-green-400">/api/ai-insights</code>
            </div>
            <p className="text-sm text-slate-400">Natural language query processing</p>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Request Format</h2>
        <div className="bg-slate-900 rounded-lg p-4">
          <pre className="text-sm text-slate-300">
{`{
  "action": "api.execute",
  "data": {
    "actionKey": "athletes.list",
    "params": {
      "limit": 10,
      "offset": 0
    },
    "user": {
      "role": "admin"
    }
  }
}`}
          </pre>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Response Format</h2>
        <div className="bg-slate-900 rounded-lg p-4">
          <pre className="text-sm text-slate-300">
{`{
  "success": true,
  "data": [...],
  "total": 100,
  "meta": {
    "count": 10,
    "executionTime": 45,
    "endpoint": "List Athletes"
  }
}`}
          </pre>
        </div>
      </div>

      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
        <h3 className="text-yellow-400 font-semibold mb-3">Rate Limits</h3>
        <ul className="space-y-2 text-slate-300 text-sm">
          <li>• 100 requests per minute per user</li>
          <li>• 1000 requests per hour per user</li>
          <li>• 429 status code returned when limit exceeded</li>
          <li>• Retry-After header indicates wait time</li>
        </ul>
      </div>
    </div>
  );
}

function APIAthletesContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Athletes API</h1>
      <p className="text-lg text-slate-400 mb-6">
        CRUD operations for athlete records
      </p>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">List Athletes</h2>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded font-mono text-sm">POST</span>
            <code className="text-green-400">/api</code>
          </div>
          <div className="bg-slate-900 rounded-lg p-4">
            <p className="text-sm text-slate-400 mb-2">Request:</p>
            <pre className="text-xs text-slate-300 overflow-x-auto">
{`{
  "action": "api.execute",
  "data": {
    "actionKey": "athletes.list",
    "params": {
      "limit": 10,
      "offset": 0,
      "district_id": "uuid-optional",
      "gender": "Male"
    }
  }
}`}
            </pre>
          </div>
          <div className="bg-slate-900 rounded-lg p-4">
            <p className="text-sm text-slate-400 mb-2">Response:</p>
            <pre className="text-xs text-slate-300 overflow-x-auto">
{`{
  "success": true,
  "data": [
    {
      "id": "uuid",
      "first_name": "John",
      "last_name": "Doe",
      "gender": "Male",
      "height_cm": 175,
      "weight_kg": 70,
      ...
    }
  ],
  "total": 100
}`}
            </pre>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Get Athlete</h2>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded font-mono text-sm">GET</span>
            <code className="text-green-400">/api?action=get-athlete&id=uuid</code>
          </div>
          <div className="bg-slate-900 rounded-lg p-4">
            <p className="text-sm text-slate-400 mb-2">Response:</p>
            <pre className="text-xs text-slate-300 overflow-x-auto">
{`{
  "success": true,
  "data": {
    "id": "uuid",
    "first_name": "John",
    "last_name": "Doe",
    "splink_id": "splink_xxx",
    "assessments": [...],
    "sri_scores": {...}
  }
}`}
            </pre>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Create Athlete</h2>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded font-mono text-sm">POST</span>
            <code className="text-green-400">/api</code>
          </div>
          <div className="bg-slate-900 rounded-lg p-4">
            <p className="text-sm text-slate-400 mb-2">Request (required fields):</p>
            <pre className="text-xs text-slate-300 overflow-x-auto">
{`{
  "action": "create-athlete",
  "data": {
    "first_name": "John",
    "last_name": "Doe",
    "gender": "Male",
    "date_of_birth": "2010-05-15",
    "height_cm": 175,
    "weight_kg": 70,
    "school_id": "uuid"
  }
}`}
            </pre>
          </div>
        </div>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
        <h3 className="text-blue-400 font-semibold mb-3">Available Filters</h3>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <code className="text-green-400">district_id</code>
          <code className="text-green-400">town_id</code>
          <code className="text-green-400">school_id</code>
          <code className="text-green-400">gender</code>
          <code className="text-green-400">min_age / max_age</code>
          <code className="text-green-400">min_height / max_height</code>
        </div>
      </div>
    </div>
  );
}

function APIAssessmentsContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Assessments API</h1>
      <p className="text-lg text-slate-400 mb-6">
        Record and retrieve athlete assessment data
      </p>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Create Assessment</h2>
        <div className="bg-slate-900 rounded-lg p-4">
          <pre className="text-xs text-slate-300 overflow-x-auto">
{`POST /api
{
  "action": "create-assessment",
  "data": {
    "athlete_id": "uuid",
    "assessment_date": "2025-12-24",
    "measurements": {
      "height_cm": 175,
      "weight_kg": 70,
      "wingspan_cm": 180,
      "sitting_height_cm": 90
    },
    "performance": {
      "sprint_30m_sec": 4.5,
      "sprint_100m_sec": 12.8,
      "vertical_jump_cm": 45,
      "broad_jump_cm": 220
    }
  }
}`}
          </pre>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Get Assessment History</h2>
        <div className="bg-slate-900 rounded-lg p-4">
          <pre className="text-xs text-slate-300 overflow-x-auto">
{`GET /api?action=get-assessments&athlete_id=uuid

Response:
{
  "success": true,
  "data": [
    {
      "id": "uuid",
      "assessment_date": "2025-12-24",
      "measurements": {...},
      "performance": {...},
      "sri_scores": {...}
    }
  ]
}`}
          </pre>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Assessment Fields</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h4 className="text-blue-400 font-semibold mb-2">Anthropometric</h4>
            <ul className="text-xs text-slate-400 space-y-1">
              <li>• height_cm, weight_kg</li>
              <li>• wingspan_cm, arm_length_cm</li>
              <li>• sitting_height_cm</li>
            </ul>
          </div>
          <div>
            <h4 className="text-green-400 font-semibold mb-2">Speed & Agility</h4>
            <ul className="text-xs text-slate-400 space-y-1">
              <li>• sprint_30m_sec, sprint_100m_sec</li>
              <li>• sprint_400m_sec</li>
              <li>• shuttle_run_sec, t_test_sec</li>
            </ul>
          </div>
          <div>
            <h4 className="text-purple-400 font-semibold mb-2">Power & Strength</h4>
            <ul className="text-xs text-slate-400 space-y-1">
              <li>• vertical_jump_cm</li>
              <li>• broad_jump_cm</li>
              <li>• shotput_distance_m</li>
              <li>• grip_strength_kg</li>
            </ul>
          </div>
          <div>
            <h4 className="text-pink-400 font-semibold mb-2">Sport-Specific</h4>
            <ul className="text-xs text-slate-400 space-y-1">
              <li>• football_freekick_m</li>
              <li>• cricket_throw_m</li>
              <li>• sit_and_reach_cm</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function APIExternalDBContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">External DB API</h1>
      <p className="text-lg text-slate-400 mb-6">
        Manage external database connections
      </p>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Test Connection</h2>
        <div className="bg-slate-900 rounded-lg p-4">
          <pre className="text-xs text-slate-300 overflow-x-auto">
{`POST /api/external-db
{
  "action": "test-connection",
  "data": {
    "db_type": "postgresql",
    "host": "db.example.com",
    "port": 5432,
    "database": "mydb",
    "username": "user",
    "password": "pass",
    "use_ssh": false
  }
}

Response:
{
  "success": true,
  "latency": 45,
  "version": "PostgreSQL 14.5"
}`}
          </pre>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Save Connection</h2>
        <div className="bg-slate-900 rounded-lg p-4">
          <pre className="text-xs text-slate-300 overflow-x-auto">
{`POST /api/external-db
{
  "action": "save-connection",
  "data": {
    "name": "School DB",
    "description": "Main database",
    "db_type": "postgresql",
    "host": "db.example.com",
    "port": 5432,
    "database": "mydb",
    "username": "readonly",
    "password": "encrypted",
    "use_ssl": true,
    "userId": "uuid"
  }
}`}
          </pre>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">List Connections</h2>
        <div className="bg-slate-900 rounded-lg p-4">
          <pre className="text-xs text-slate-300 overflow-x-auto">
{`POST /api/external-db
{
  "action": "list-connections",
  "data": { "userId": "uuid" }
}

Response:
{
  "success": true,
  "connections": [
    {
      "id": "uuid",
      "name": "School DB",
      "db_type": "postgresql",
      "status": "active",
      "last_tested": "2025-12-24T10:30:00Z"
    }
  ]
}`}
          </pre>
        </div>
      </div>

      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
        <h3 className="text-yellow-400 font-semibold mb-3">🔒 Security Notes</h3>
        <ul className="space-y-2 text-slate-300 text-sm">
          <li>• Passwords encrypted with bcrypt before storage</li>
          <li>• SSH keys stored in Vercel KV (encrypted)</li>
          <li>• All connections tested before saving</li>
          <li>• Admin role required for all operations</li>
        </ul>
      </div>
    </div>
  );
}

function APIDataImportContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Data Import API</h1>
      <p className="text-lg text-slate-400 mb-6">
        Import CSV/Excel data programmatically
      </p>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Parse File</h2>
        <div className="bg-slate-900 rounded-lg p-4">
          <pre className="text-xs text-slate-300 overflow-x-auto">
{`POST /api/data-import
{
  "action": "parse-file",
  "data": {
    "file": "base64_encoded_file",
    "fileName": "athletes.csv",
    "fileType": "text/csv"
  }
}

Response:
{
  "success": true,
  "preview": [...first 5 rows...],
  "columns": ["name", "height", "weight"],
  "totalRows": 100
}`}
          </pre>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Validate Schema</h2>
        <div className="bg-slate-900 rounded-lg p-4">
          <pre className="text-xs text-slate-300 overflow-x-auto">
{`POST /api/data-import
{
  "action": "validate-schema",
  "data": {
    "parsedData": [...],
    "columnMapping": {
      "name": "first_name",
      "height": "height_cm"
    }
  }
}

Response:
{
  "success": true,
  "validRecords": 95,
  "invalidRecords": 5,
  "errors": [
    {
      "row": 10,
      "field": "height_cm",
      "error": "Invalid value"
    }
  ]
}`}
          </pre>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Create Import</h2>
        <div className="bg-slate-900 rounded-lg p-4">
          <pre className="text-xs text-slate-300 overflow-x-auto">
{`POST /api/data-import
{
  "action": "create-import",
  "data": {
    "name": "School A Athletes",
    "description": "2025 assessment data",
    "validatedData": [...],
    "columnMapping": {...},
    "userId": "uuid"
  }
}

Response:
{
  "success": true,
  "importId": "uuid",
  "tableName": "athletes_import_abc123",
  "recordsImported": 95
}`}
          </pre>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">List Imports</h2>
        <div className="bg-slate-900 rounded-lg p-4">
          <pre className="text-xs text-slate-300 overflow-x-auto">
{`POST /api/data-import
{
  "action": "list-imports",
  "data": { "userId": "uuid" }
}

Response:
{
  "success": true,
  "imports": [
    {
      "id": "uuid",
      "name": "School A Athletes",
      "status": "completed",
      "valid_records": 95,
      "created_at": "2025-12-24T10:00:00Z"
    }
  ]
}`}
          </pre>
        </div>
      </div>
    </div>
  );
}

function CorrelationEngineContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Correlation Engine</h1>
      <p className="text-lg text-slate-400 mb-6">
        AI-powered sport suitability analysis
      </p>

      <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/30 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">How It Works</h2>
        <p className="text-slate-300 mb-4">
          The Correlation Engine analyzes athlete physical attributes and performance metrics to determine suitability for different sports. It uses machine learning models trained on professional athlete data to identify patterns and predict sport-specific potential.
        </p>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Sport Categories</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">⚽ Team Sports</h4>
            <ul className="text-xs text-slate-400 space-y-1">
              <li>• Football (Soccer)</li>
              <li>• Basketball</li>
              <li>• Volleyball</li>
              <li>• Hockey</li>
            </ul>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-2">🏃 Track & Field</h4>
            <ul className="text-xs text-slate-400 space-y-1">
              <li>• Sprinting (100m, 200m, 400m)</li>
              <li>• Middle Distance (800m, 1500m)</li>
              <li>• Long Jump, High Jump</li>
              <li>• Throwing Events</li>
            </ul>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">🥊 Combat Sports</h4>
            <ul className="text-xs text-slate-400 space-y-1">
              <li>• Boxing</li>
              <li>• Wrestling</li>
              <li>• Judo</li>
              <li>• Taekwondo</li>
            </ul>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-pink-400 font-semibold mb-2">🎾 Racquet Sports</h4>
            <ul className="text-xs text-slate-400 space-y-1">
              <li>• Tennis</li>
              <li>• Badminton</li>
              <li>• Table Tennis</li>
              <li>• Squash</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Sport-Specific Correlations</h2>
        <p className="text-slate-300 mb-4">
          Detailed analysis of how physical attributes correlate with sport performance
        </p>
        
        <div className="space-y-4">
          {/* Basketball */}
          <div className="bg-gradient-to-r from-blue-500/10 to-blue-500/5 rounded-lg border border-blue-500/30 p-4">
            <h4 className="text-blue-400 font-bold text-lg mb-3">🏀 Basketball</h4>
            <div className="space-y-2 text-sm">
              <div className="bg-slate-900 rounded p-3">
                <p className="text-white font-semibold mb-1">Height → Rebounding & Shot Blocking</p>
                <p className="text-slate-400">Strong positive correlation (r=0.85). Taller players have significant advantage in defensive positioning and offensive rebounds.</p>
              </div>
              <div className="bg-slate-900 rounded p-3">
                <p className="text-white font-semibold mb-1">Vertical Jump → Scoring Ability</p>
                <p className="text-slate-400">Moderate correlation (r=0.68). Higher vertical jump enables dunking, better shooting over defenders, and rim protection.</p>
              </div>
              <div className="bg-slate-900 rounded p-3">
                <p className="text-white font-semibold mb-1">Wingspan (Ape Index) → Defensive Rating</p>
                <p className="text-slate-400">Strong correlation (r=0.79). Longer wingspan improves steal rate, shot contests, and passing lane disruption.</p>
              </div>
              <div className="bg-slate-900 rounded p-3">
                <p className="text-white font-semibold mb-1">Agility (T-Test) → Ball Handling</p>
                <p className="text-slate-400">Moderate correlation (r=0.62). Better agility correlates with dribbling ability and defensive footwork.</p>
              </div>
            </div>
          </div>

          {/* Swimming */}
          <div className="bg-gradient-to-r from-cyan-500/10 to-cyan-500/5 rounded-lg border border-cyan-500/30 p-4">
            <h4 className="text-cyan-400 font-bold text-lg mb-3">🏊 Swimming</h4>
            <div className="space-y-2 text-sm">
              <div className="bg-slate-900 rounded p-3">
                <p className="text-white font-semibold mb-1">Wingspan → Stroke Length</p>
                <p className="text-slate-400">Very strong correlation (r=0.88). Longer arms create more propulsion per stroke, directly improving speed and efficiency.</p>
              </div>
              <div className="bg-slate-900 rounded p-3">
                <p className="text-white font-semibold mb-1">Height + Long Legs → Freestyle/Backstroke</p>
                <p className="text-slate-400">Strong correlation (r=0.81). Taller swimmers with longer legs have better kick propulsion and body position in water.</p>
              </div>
              <div className="bg-slate-900 rounded p-3">
                <p className="text-white font-semibold mb-1">Sitting Height Ratio → Breaststroke</p>
                <p className="text-slate-400">Moderate correlation (r=0.64). Longer torso relative to legs provides advantage in breaststroke technique.</p>
              </div>
              <div className="bg-slate-900 rounded p-3">
                <p className="text-white font-semibold mb-1">Shoulder Width → Butterfly</p>
                <p className="text-slate-400">Strong correlation (r=0.76). Wider shoulders generate more power in butterfly stroke's simultaneous arm movement.</p>
              </div>
            </div>
          </div>

          {/* Sprinting */}
          <div className="bg-gradient-to-r from-green-500/10 to-green-500/5 rounded-lg border border-green-500/30 p-4">
            <h4 className="text-green-400 font-bold text-lg mb-3">⚡ Sprinting (100m/200m)</h4>
            <div className="space-y-2 text-sm">
              <div className="bg-slate-900 rounded p-3">
                <p className="text-white font-semibold mb-1">Power-to-Weight Ratio → Acceleration</p>
                <p className="text-slate-400">Very strong correlation (r=0.91). Higher ratio enables explosive starts and faster acceleration phase (0-30m).</p>
              </div>
              <div className="bg-slate-900 rounded p-3">
                <p className="text-white font-semibold mb-1">Leg Length → Stride Length</p>
                <p className="text-slate-400">Strong correlation (r=0.77). Longer legs allow for greater stride length, reducing steps needed to cover distance.</p>
              </div>
              <div className="bg-slate-900 rounded p-3">
                <p className="text-white font-semibold mb-1">Vertical Jump → Sprint Speed</p>
                <p className="text-slate-400">Moderate correlation (r=0.69). Vertical jump indicates leg power, which translates to horizontal propulsion in sprinting.</p>
              </div>
              <div className="bg-slate-900 rounded p-3">
                <p className="text-white font-semibold mb-1">BMI (Lower) → Top Speed</p>
                <p className="text-slate-400">Negative correlation (r=-0.58). Lower BMI (lean muscle) reduces drag and improves speed maintenance.</p>
              </div>
            </div>
          </div>

          {/* Volleyball */}
          <div className="bg-gradient-to-r from-purple-500/10 to-purple-500/5 rounded-lg border border-purple-500/30 p-4">
            <h4 className="text-purple-400 font-bold text-lg mb-3">🏐 Volleyball</h4>
            <div className="space-y-2 text-sm">
              <div className="bg-slate-900 rounded p-3">
                <p className="text-white font-semibold mb-1">Height + Vertical Jump → Blocking</p>
                <p className="text-slate-400">Combined strong correlation (r=0.86). Reach height (standing + jump) is critical for effective blocking at the net.</p>
              </div>
              <div className="bg-slate-900 rounded p-3">
                <p className="text-white font-semibold mb-1">Broad Jump → Spiking Power</p>
                <p className="text-slate-400">Strong correlation (r=0.74). Horizontal power translates to explosive approach and powerful attacks.</p>
              </div>
              <div className="bg-slate-900 rounded p-3">
                <p className="text-white font-semibold mb-1">Arm Length → Serving Speed</p>
                <p className="text-slate-400">Moderate correlation (r=0.67). Longer arms create greater leverage for powerful jump serves.</p>
              </div>
            </div>
          </div>

          {/* Boxing */}
          <div className="bg-gradient-to-r from-red-500/10 to-red-500/5 rounded-lg border border-red-500/30 p-4">
            <h4 className="text-red-400 font-bold text-lg mb-3">🥊 Boxing</h4>
            <div className="space-y-2 text-sm">
              <div className="bg-slate-900 rounded p-3">
                <p className="text-white font-semibold mb-1">Ape Index (Reach) → Striking Range</p>
                <p className="text-slate-400">Very strong correlation (r=0.89). Longer reach allows fighters to land punches while staying outside opponent's range.</p>
              </div>
              <div className="bg-slate-900 rounded p-3">
                <p className="text-white font-semibold mb-1">Grip Strength → Punching Power</p>
                <p className="text-slate-400">Strong correlation (r=0.72). Grip strength indicates overall upper body power and punch impact force.</p>
              </div>
              <div className="bg-slate-900 rounded p-3">
                <p className="text-white font-semibold mb-1">Agility (Shuttle Run) → Defensive Movement</p>
                <p className="text-slate-400">Strong correlation (r=0.78). Better agility enables effective head movement, slips, and counter-punching.</p>
              </div>
            </div>
          </div>

          {/* Long Jump */}
          <div className="bg-gradient-to-r from-yellow-500/10 to-yellow-500/5 rounded-lg border border-yellow-500/30 p-4">
            <h4 className="text-yellow-400 font-bold text-lg mb-3">🦘 Long Jump</h4>
            <div className="space-y-2 text-sm">
              <div className="bg-slate-900 rounded p-3">
                <p className="text-white font-semibold mb-1">Sprint Speed (30m) → Approach Velocity</p>
                <p className="text-slate-400">Very strong correlation (r=0.92). Faster approach speed directly translates to greater jump distance.</p>
              </div>
              <div className="bg-slate-900 rounded p-3">
                <p className="text-white font-semibold mb-1">Vertical Jump → Takeoff Power</p>
                <p className="text-slate-400">Strong correlation (r=0.83). Vertical power converts to horizontal distance through proper takeoff angle.</p>
              </div>
              <div className="bg-slate-900 rounded p-3">
                <p className="text-white font-semibold mb-1">Leg Length → Flight Distance</p>
                <p className="text-slate-400">Moderate correlation (r=0.66). Longer legs provide leverage advantage during takeoff phase.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Correlation Scores</h2>
        <p className="text-slate-300 mb-3">Athletes receive correlation scores (0-100) for each sport:</p>
        <div className="space-y-2">
          <div className="flex items-center justify-between bg-slate-900 rounded-lg p-3">
            <span className="text-slate-300">90-100</span>
            <span className="text-green-400 font-semibold">Exceptional Fit</span>
          </div>
          <div className="flex items-center justify-between bg-slate-900 rounded-lg p-3">
            <span className="text-slate-300">75-89</span>
            <span className="text-blue-400 font-semibold">Strong Potential</span>
          </div>
          <div className="flex items-center justify-between bg-slate-900 rounded-lg p-3">
            <span className="text-slate-300">60-74</span>
            <span className="text-yellow-400 font-semibold">Good Fit</span>
          </div>
          <div className="flex items-center justify-between bg-slate-900 rounded-lg p-3">
            <span className="text-slate-300">Below 60</span>
            <span className="text-slate-400 font-semibold">Limited Correlation</span>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Understanding Correlation Coefficients</h2>
        <div className="space-y-3">
          <div className="bg-slate-900 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-semibold">r = 0.90 - 1.00</span>
              <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">Very Strong</span>
            </div>
            <p className="text-sm text-slate-400">Nearly perfect relationship - attribute is critical for sport success</p>
          </div>
          <div className="bg-slate-900 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-semibold">r = 0.70 - 0.89</span>
              <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs">Strong</span>
            </div>
            <p className="text-sm text-slate-400">Significant relationship - attribute provides major advantage</p>
          </div>
          <div className="bg-slate-900 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-semibold">r = 0.50 - 0.69</span>
              <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs">Moderate</span>
            </div>
            <p className="text-sm text-slate-400">Noticeable relationship - attribute is beneficial but not decisive</p>
          </div>
          <div className="bg-slate-900 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-semibold">r = 0.30 - 0.49</span>
              <span className="px-3 py-1 bg-slate-500/20 text-slate-400 rounded-full text-xs">Weak</span>
            </div>
            <p className="text-sm text-slate-400">Minimal relationship - attribute has limited impact</p>
          </div>
        </div>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
        <h3 className="text-blue-400 font-semibold mb-3">Model Training & Data Sources</h3>
        <ul className="space-y-2 text-slate-300 text-sm">
          <li>• Trained on 50,000+ professional athlete profiles from Olympic and national databases</li>
          <li>• Continuous learning from new assessment data collected in the platform</li>
          <li>• Sport-specific feature importance analysis using Random Forest and XGBoost</li>
          <li>• Regular model updates and cross-validation against competition results</li>
          <li>• Correlation coefficients derived from peer-reviewed sports science research</li>
        </ul>
      </div>
    </div>
  );
}

function VisualizationContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Data Visualization</h1>
      <p className="text-lg text-slate-400 mb-6">
        Interactive charts and visual analytics
      </p>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Chart Types</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/30 p-4">
            <h4 className="text-blue-400 font-semibold mb-2">📊 Bar Charts</h4>
            <p className="text-sm text-slate-400">Gender distribution, district comparisons, performance metrics</p>
          </div>

          <div className="bg-gradient-to-br from-green-500/10 to-blue-500/10 rounded-lg border border-green-500/30 p-4">
            <h4 className="text-green-400 font-semibold mb-2">🥧 Pie Charts</h4>
            <p className="text-sm text-slate-400">Sport suitability breakdown, assessment completion rates</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-lg border border-purple-500/30 p-4">
            <h4 className="text-purple-400 font-semibold mb-2">📈 Line Charts</h4>
            <p className="text-sm text-slate-400">Progress tracking over time, performance trends</p>
          </div>

          <div className="bg-gradient-to-br from-pink-500/10 to-red-500/10 rounded-lg border border-pink-500/30 p-4">
            <h4 className="text-pink-400 font-semibold mb-2">🔵 Scatter Plots</h4>
            <p className="text-sm text-slate-400">Height vs weight, correlation analysis</p>
          </div>

          <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-lg border border-yellow-500/30 p-4">
            <h4 className="text-yellow-400 font-semibold mb-2">🟩 Heatmaps</h4>
            <p className="text-sm text-slate-400">Performance distribution across districts</p>
          </div>

          <div className="bg-gradient-to-br from-red-500/10 to-pink-500/10 rounded-lg border border-red-500/30 p-4">
            <h4 className="text-red-400 font-semibold mb-2">🎯 Radar Charts</h4>
            <p className="text-sm text-slate-400">Multi-dimensional athlete profiles</p>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Visualization Libraries</h2>
        <div className="space-y-3">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">Chart.js</h4>
            <p className="text-sm text-slate-400">Primary charting library for bar, line, and pie charts</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-2">Recharts</h4>
            <p className="text-sm text-slate-400">React-based charts for complex visualizations</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">D3.js</h4>
            <p className="text-sm text-slate-400">Custom visualizations and data transformations</p>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Interactive Features</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">Hover Tooltips</h4>
            <p className="text-sm text-slate-400">Detailed information on hover</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-2">Click-to-Filter</h4>
            <p className="text-sm text-slate-400">Click chart elements to filter data</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">Zoom & Pan</h4>
            <p className="text-sm text-slate-400">Explore large datasets interactively</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-pink-400 font-semibold mb-2">Export</h4>
            <p className="text-sm text-slate-400">Download charts as PNG/SVG</p>
          </div>
        </div>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
        <h3 className="text-blue-400 font-semibold mb-3">Dashboard Widgets</h3>
        <ul className="space-y-2 text-slate-300 text-sm">
          <li>• Real-time statistics cards (total athletes, avg height, etc.)</li>
          <li>• Gender distribution pie chart</li>
          <li>• District-wise athlete count bar chart</li>
          <li>• Performance metrics comparison</li>
          <li>• Sport suitability radar chart</li>
        </ul>
      </div>
    </div>
  );
}

function ExportReportsContent() {
  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-white mb-2">Export & Reports</h1>
      <p className="text-lg text-slate-400 mb-6">
        Generate and export athlete reports in multiple formats
      </p>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Export Formats</h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-red-500/10 to-pink-500/10 rounded-lg border border-red-500/30 p-4 text-center">
            <div className="text-4xl mb-2">📊</div>
            <h4 className="text-red-400 font-semibold mb-1">CSV</h4>
            <p className="text-xs text-slate-400">Spreadsheet format</p>
          </div>

          <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/30 p-4 text-center">
            <div className="text-4xl mb-2">📄</div>
            <h4 className="text-blue-400 font-semibold mb-1">PDF</h4>
            <p className="text-xs text-slate-400">Print-ready reports</p>
          </div>

          <div className="bg-gradient-to-br from-green-500/10 to-blue-500/10 rounded-lg border border-green-500/30 p-4 text-center">
            <div className="text-4xl mb-2">📗</div>
            <h4 className="text-green-400 font-semibold mb-1">Excel</h4>
            <p className="text-xs text-slate-400">Advanced analysis</p>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Report Types</h2>
        <div className="space-y-3">
          <div className="bg-gradient-to-r from-blue-500/10 to-blue-500/5 rounded-lg border border-blue-500/30 p-4">
            <h4 className="text-blue-400 font-semibold mb-2">📊 Individual Athlete Report</h4>
            <p className="text-sm text-slate-400 mb-2">Comprehensive profile with all assessments and sport recommendations</p>
            <ul className="text-xs text-slate-400 space-y-1 ml-4">
              <li>• Personal information and demographics</li>
              <li>• Physical measurements and derived metrics</li>
              <li>• Performance test results</li>
              <li>• Sport suitability analysis</li>
              <li>• Progress charts (if multiple assessments)</li>
            </ul>
          </div>

          <div className="bg-gradient-to-r from-green-500/10 to-green-500/5 rounded-lg border border-green-500/30 p-4">
            <h4 className="text-green-400 font-semibold mb-2">🏫 School/District Report</h4>
            <p className="text-sm text-slate-400 mb-2">Aggregate statistics for a school or district</p>
            <ul className="text-xs text-slate-400 space-y-1 ml-4">
              <li>• Total athletes assessed</li>
              <li>• Average performance metrics</li>
              <li>• Gender distribution</li>
              <li>• Top performers by sport</li>
              <li>• Comparison with other districts</li>
            </ul>
          </div>

          <div className="bg-gradient-to-r from-purple-500/10 to-purple-500/5 rounded-lg border border-purple-500/30 p-4">
            <h4 className="text-purple-400 font-semibold mb-2">🏆 Talent Identification Report</h4>
            <p className="text-sm text-slate-400 mb-2">Athletes with high potential for specific sports</p>
            <ul className="text-xs text-slate-400 space-y-1 ml-4">
              <li>• Filtered by sport and correlation score</li>
              <li>• Ranked list of top candidates</li>
              <li>• Key strengths highlighted</li>
              <li>• Contact information (if available)</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">PDF Report Features</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2">Professional Layout</h4>
            <p className="text-sm text-slate-400">Clean, branded design with logo and headers</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-2">Charts & Graphs</h4>
            <p className="text-sm text-slate-400">Visual representations of data</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold mb-2">Multi-Page</h4>
            <p className="text-sm text-slate-400">Automatic pagination for large reports</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4">
            <h4 className="text-pink-400 font-semibold mb-2">Watermark</h4>
            <p className="text-sm text-slate-400">Optional SRI branding watermark</p>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Bulk Export</h2>
        <p className="text-slate-300 mb-3">Export multiple athlete reports at once:</p>
        <div className="space-y-2">
          <div className="bg-slate-900 rounded-lg p-3">
            <p className="text-sm text-slate-300">• Select athletes from Manual Explorer</p>
          </div>
          <div className="bg-slate-900 rounded-lg p-3">
            <p className="text-sm text-slate-300">• Choose export format (CSV/PDF/Excel)</p>
          </div>
          <div className="bg-slate-900 rounded-lg p-3">
            <p className="text-sm text-slate-300">• Generate ZIP file with all reports</p>
          </div>
          <div className="bg-slate-900 rounded-lg p-3">
            <p className="text-sm text-slate-300">• Download or email to recipients</p>
          </div>
        </div>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
        <h3 className="text-blue-400 font-semibold mb-3">Export Libraries</h3>
        <ul className="space-y-2 text-slate-300 text-sm">
          <li>• <strong>jsPDF:</strong> PDF generation with charts</li>
          <li>• <strong>html2canvas:</strong> Convert HTML to images for PDFs</li>
          <li>• <strong>PapaParse:</strong> CSV export with proper formatting</li>
          <li>• <strong>XLSX:</strong> Excel file generation</li>
        </ul>
      </div>
    </div>
  );
}
